import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { workOrderApi } from '../../api/workOrderApi';
import WorkOrderFormModal from './WorkOrderFormModal';
import WorkOrderDetailModal from './WorkOrderDetailModal';
import WorkOrderReportDetailModal from './WorkOrderReportDetailModal';
import WorkOrderReportFormModal from './WorkOrderReportFormModal';
import './WorkOrderListPage.css';

// ── 상태 메타 정보 ──────────────────────────────────────────────────
const PRIORITY_META = {
  urgent: { label: '긴급', color: '#dc3545' },
  high: { label: '높음', color: '#fd7e14' },
  normal: { label: '보통', color: '#0d6efd' },
  low: { label: '낮음', color: '#6c757d' },
};

// ── D-Day 계산 ────────────────────────────────────────────────────
const getDDay = (dueDateStr, item = null) => {
  if (item) {
    if (item.deleted_at || item.status === 'deleted') {
      return { text: '삭제', className: 'text-secondary fw-bold' };
    }
    if (['cancel', 'canceled'].includes(item.status)) {
      return { text: '취소', className: 'text-secondary fw-bold' };
    }
    if (item.status === 'stop') {
      return { text: '중단', className: 'text-warning fw-bold' };
    }
    if (['done', 'completed'].includes(item.status)) {
      return { text: '완료', className: 'text-success fw-bold' };
    }
    if (item.status === 'temp' || item.status === 'draft') {
      return null;
    }
  }
  if (!dueDateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr);
  due.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (diff > 0) return { text: `D-${diff}`, className: diff <= 3 ? 'text-warning fw-bold' : 'text-muted' };
  if (diff === 0) return { text: 'D-Day', className: 'text-danger fw-bold' };
  return { text: `지연 +${Math.abs(diff)}`, className: 'text-danger fw-bold' };
};

// ── 탭별 메타 ─────────────────────────────────────────────────────
const TAB_META = {
  received: { title: '요청받은 업무', icon: 'fa-inbox', desc: '내가 담당자로 지정된 협조 업무' },
  sent: { title: '요청한 업무', icon: 'fa-paper-plane', desc: '내가 요청한 협조 업무' },
  referred: { title: '참조 업무', icon: 'fa-eye', desc: '내가 참조자로 지정된 업무' },
  completed: { title: '완료된 업무', icon: 'fa-check-circle', desc: '종결된 업무 히스토리' },
  temp: { title: '임시저장', icon: 'fa-save', desc: '등록 전 임시저장 목록' },
  reports: { title: '처리결과 목록', icon: 'fa-file-alt', desc: '제출된 처리결과 검토 및 확인' },
  canceled: { title: '취소/삭제된 업무', icon: 'fa-trash-alt', desc: '취소되거나 삭제된 업무 보관함' },
};

const SEARCH_DOMAIN_OPTIONS = [
  { value: 'order', label: '업무협조' },
  { value: 'report', label: '처리결과' },
];

const SEARCH_ORDER_TYPE_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'title', label: '제목' },
  { value: 'content', label: '본문' },
  { value: 'author', label: '요청자' },
  { value: 'assignee', label: '담당자' },
];

const SEARCH_REPORT_TYPE_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'title', label: '제목' },
  { value: 'content', label: '내용' },
  { value: 'author', label: '작성자' },
];

// 탭별 지원 상태 옵션 (진행중 업무)
const STATUS_OPTIONS_BY_TAB = {
  received: [
    { value: 'all', label: '전체 상태' },
    { value: 'stop', label: '중단' },
    { value: 'request', label: '대기(미접수)' },
    { value: 'progress', label: '진행중' },
    { value: 'review', label: '검토 대기' },
    { value: 'delay', label: '지연' }
  ],
  sent: [
    { value: 'all', label: '전체 상태' },
    { value: 'stop', label: '중단' },
    { value: 'request', label: '대기(미접수)' },
    { value: 'progress', label: '진행중' },
    { value: 'review', label: '검토 대기' },
    { value: 'delay', label: '지연' }
  ],
  referred: [
    { value: 'all', label: '전체 상태' },
    { value: 'stop', label: '중단' },
    { value: 'progress', label: '진행중' },
    { value: 'delay', label: '지연' }
  ]
};

// 완료된 업무 전용 4개 서브 탭
const COMPLETED_SUB_TABS = [
  { key: 'all', label: '전체', icon: 'fa-layer-group' },
  { key: 'received', label: '요청받은 업무', icon: 'fa-inbox' },
  { key: 'sent', label: '요청한 업무', icon: 'fa-paper-plane' },
  { key: 'referred', label: '참조 업무', icon: 'fa-eye' }
];

function WorkOrderListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useContext(AuthContext);

  // URL 경로에서 탭 추출
  const pathToTab = {
    '/work-order/received': 'received',
    '/work-order/sent': 'sent',
    '/work-order/referred': 'referred',
    '/work-order/completed': 'completed',
    '/work-order/temp': 'temp',
    '/work-order/reports': 'reports',
    '/work-order/canceled': 'canceled',
  };
  const tab = pathToTab[location.pathname] || 'received';
  const meta = TAB_META[tab] || TAB_META.received;

  // ── 권한 판별 (관리자 3단 권한 분기) ──
  // 1) 최고관리자 (userid === 'admin' && role === 'admin'): 체크박스 미노출, 항상 전사 전체 업무 표출
  // 2) 일반관리자 (userid !== 'admin' && role === 'admin'): 체크박스 노출, 체크 시 전사 전체 업무 / 미체크 시 본인 업무
  // 3) 일반사용자 (role !== 'admin'): 체크박스 미노출, 본인 업무만 표출
  const isSuperAdmin = (user?.userid === 'admin' || user?.id === 'admin') && (user?.role === 'admin' || user?.is_admin === 1 || user?.is_admin === true);
  const isRegularAdmin = !isSuperAdmin && (user?.role === 'admin' || user?.is_admin === 1 || user?.is_admin === true);
  const isAdminRole = isRegularAdmin;

  // URL 쿼리 파라미터 초기값 직접 파싱 (마운트 시 레이스 컨디션 및 깜빡임 제거)
  const [adminAllChecked, setAdminAllChecked] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('adminAll') === 'true';
  });
  const effectiveAdminAll = isSuperAdmin ? true : (isRegularAdmin ? adminAllChecked : false);

  // ── 상태 ──────────────────────────────────────────────────────
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [tabCounts, setTabCounts] = useState({ received: 0, sent: 0, referred: 0 });
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('status') || 'all';
  });
  const [completedSubTab, setCompletedSubTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('status') || 'all';
  });
  const [completedCounts, setCompletedCounts] = useState({ all: 0, received: 0, sent: 0, referred: 0 });
  const [page, setPage] = useState(1);
  const LIMIT = 10;

  const [searchDomain, setSearchDomain] = useState('order');
  const [searchType, setSearchType] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchParams, setSearchParams] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [hoveredAttendees, setHoveredAttendees] = useState(null);
  const [hoveredStatus, setHoveredStatus] = useState(null);
  const hoverStatusTimerRef = useRef(null);
  const [filePopover, setFilePopover] = useState({ id: null, x: 0, y: 0, isUp: false });

  // ── 연도 필터 상태 (기본: 전체 연도, [현재+1, 현재, 현재-1, 현재-2] 4개년 관리) ──
  const [selectedYear, setSelectedYear] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const y = urlParams.get('year');
    if (!y || y === 'all') return 'all';
    return parseInt(y, 10);
  });
  const [yearList, setYearList] = useState(() => {
    const cy = new Date().getFullYear();
    return [cy + 1, cy, cy - 1, cy - 2];
  });
  const [isYearOpen, setIsYearOpen] = useState(false);
  const yearDropdownRef = useRef(null);

  // ── 상태별 실시간 건수 및 커스텀 상태 드롭다운 상태 ──
  const [statusCounts, setStatusCounts] = useState({});
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  // 과거 3년 추가 핸들러
  const handleAddPastYears = (e) => {
    e.stopPropagation();
    setYearList(prev => {
      const minYear = Math.min(...prev);
      const newYears = [minYear - 1, minYear - 2, minYear - 3];
      return [...prev, ...newYears];
    });
  };

  // 과거 3년 축소 핸들러 (기본 4개년은 유지)
  const handleRemovePastYears = (e) => {
    e.stopPropagation();
    setYearList(prev => {
      if (prev.length <= 4) return prev;
      return prev.slice(0, prev.length - 3);
    });
  };

  // 기본 4개년 및 전체 연도로 초기화
  const handleResetYears = (e) => {
    e.stopPropagation();
    const cy = new Date().getFullYear();
    setYearList([cy + 1, cy, cy - 1, cy - 2]);
    setSelectedYear('all');
    setIsYearOpen(false);
    setPage(1);
  };

  // 팝오버 및 연도/상태 드롭다운 외부 클릭 시 닫기 (불필요한 리렌더 방지)
  useEffect(() => {
    const handleOutsideClick = (e) => {
      setFilePopover(prev => (prev.id !== null ? { id: null, x: 0, y: 0, isUp: false } : prev));
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(e.target)) {
        setIsYearOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setIsStatusOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleDownloadFile = async (file) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      const filePath = file.file_path || file.path || '';
      const url = domain + (filePath ? filePath.replace(/\\/g, '/') : '');
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name || file.originalName || file.name || 'download';
      a.target = '_blank';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 100);
    } catch (e) {
      console.error(e);
      alert('다운로드 중 오류가 발생했습니다.');
    }
  };

  const handleViewFile = async (file) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      const filePath = file.file_path || file.path || '';
      const url = domain + (filePath ? filePath.replace(/\\/g, '/') : '');
      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
      alert('파일 열기 중 오류가 발생했습니다.');
    }
  };

  const handleZipDownloadFiles = async (files, title) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      alert(`${files.length}개의 파일을 압축 중입니다. 잠시만 기다려 주세요...`);
      const JSZip = (await import('jszip')).default;
      const { saveAs } = await import('file-saver');
      const zip = new JSZip();
      const promises = files.map(async (f) => {
        const filePath = f.file_path || f.path || '';
        const url = domain + (filePath ? filePath.replace(/\\/g, '/') : '');
        const response = await fetch(url);
        const blob = await response.blob();
        zip.file(f.file_name || f.originalName || f.name || 'file', blob);
      });
      await Promise.all(promises);
      const content = await zip.generateAsync({ type: 'blob' });
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      saveAs(content, `${title ? title.replace(/[/\\:*?"<>|]/g, '_') : '첨부파일'}_${dateStr}.zip`);
    } catch (e) {
      console.error(e);
      alert('ZIP 압축 다운로드 중 오류가 발생했습니다.');
    }
  };

  // ── 모달 상태 ──────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'view' | 'edit'
  const [modalType, setModalType] = useState('detail'); // 'detail' (상세조회) | 'form' (단독양식/임시저장)
  const [modalWorkOrderId, setModalWorkOrderId] = useState(null);
  const [modalInitialTab, setModalInitialTab] = useState(null);
  const [modalFocusId, setModalFocusId] = useState(null);

  // ── 자식 보고서 상세 모달 상태 ──
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetailOpen, setReportDetailOpen] = useState(false);
  const [reportParentWorkOrderId, setReportParentWorkOrderId] = useState(null);

  // ── 독립 결과보고서 작성 모달 상태 ──
  const [reportFormModalState, setReportFormModalState] = useState({ open: false, workOrderId: null, workOrder: null });

  // ── 계층형 트리 그리드 접기/펼치기 상태 ──
  const [expandedRows, setExpandedRows] = useState({});
  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpandedRows(prev => {
      const currentVal = Boolean(prev[id]);
      return {
        ...prev,
        [id]: !currentVal
      };
    });
  };

  // 보고서가 있는 업무지시 목록 및 일괄 펼침/접기 상태
  const itemsWithReports = useMemo(() => {
    const currentUserId = String(user?.id || user?.userid || '');
    const isSuperAdmin = user?.is_admin === true || user?.is_admin === 1 || user?.role === 'admin';
    return (list || []).filter(item => {
      const visibleReports = (item.reports || []).filter(rep => {
        const repStatus = rep.approval_status || rep.status;
        const repUserId = String(rep.user_id || rep.reporter?.id || rep.reporter?.userid || '');
        if (repStatus === 'draft' || repStatus === 'temp') {
          return isSuperAdmin || (currentUserId && repUserId === currentUserId);
        }
        const isPrivate = rep.is_public === false || rep.is_public === 0;
        if (isPrivate) {
          const isAuthor = String(item.author_id) === currentUserId;
          const isMyRep = repUserId === currentUserId;
          if (!isSuperAdmin && !isAuthor && !isMyRep) return false;
        }
        return true;
      });
      return visibleReports.length > 0;
    });
  }, [list, user]);

  const hasAnyReports = itemsWithReports.length > 0;
  // 모든 항목이 펼쳐져 있는지 여부 (기본 접힘: 모든 항목이 true일 때만 일괄 접기 가능)
  const isAllExpanded = hasAnyReports && itemsWithReports.every(item => Boolean(expandedRows[item.id]));

  const toggleAllReports = (e) => {
    e.stopPropagation();
    setExpandedRows(prev => {
      const next = { ...prev };
      itemsWithReports.forEach(item => {
        next[item.id] = !isAllExpanded;
      });
      return next;
    });
  };

  const openCreateModal = useCallback(() => {
    setModalType('form');
    setModalMode('create');
    setModalWorkOrderId(null);
    setModalInitialTab(null);
    setModalFocusId(null);
    setModalOpen(true);
  }, []);
  const openViewModal = useCallback((id, isTemp = false, initialTab = null) => {
    setModalType(isTemp ? 'form' : 'detail');
    setModalMode('view');
    setModalWorkOrderId(id);
    setModalInitialTab(initialTab);
    setModalFocusId(null);
    setModalOpen(true);
  }, []);
  const openEditModal = useCallback((id) => {
    setModalType('form');
    setModalMode('edit');
    setModalWorkOrderId(id);
    setModalInitialTab(null);
    setModalFocusId(null);
    setModalOpen(true);
  }, []);
  const switchToEdit = useCallback(() => { setModalType('form'); setModalMode('edit'); }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalType('detail');
    setModalMode('create');
    setModalWorkOrderId(null);
    setModalInitialTab(null);
    setModalFocusId(null);
    // URL에 workOrderId 또는 detailId가 있는 경우 쿼리 파라미터 정리
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('workOrderId') || urlParams.has('detailId') || urlParams.has('focusId') || urlParams.has('reportId')) {
      urlParams.delete('workOrderId');
      urlParams.delete('detailId');
      urlParams.delete('tab');
      urlParams.delete('focusId');
      urlParams.delete('reportId');
      const newSearch = urlParams.toString();
      navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // ── 목록 조회 ─────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      const urlParams = new URLSearchParams(location.search);
      const urlStatus = urlParams.get('status');

      let effectiveStatus = undefined;
      if (tab === 'completed') {
        const sub = completedSubTab !== 'all' ? completedSubTab : (urlStatus && urlStatus !== 'all' ? urlStatus : undefined);
        effectiveStatus = sub;
      } else if (['received', 'sent', 'referred'].includes(tab)) {
        const st = selectedStatus !== 'all' ? selectedStatus : (urlStatus && urlStatus !== 'all' ? urlStatus : undefined);
        effectiveStatus = st;
      }

      const params = {
        type: tab,
        tab,
        page,
        limit: LIMIT,
        ...(selectedYear && selectedYear !== 'all' ? { year: selectedYear } : {}),
        ...(effectiveAdminAll ? { adminAll: true } : {}),
        ...(effectiveStatus ? { status: effectiveStatus } : {}),
        ...searchParams
      };
      const res = await workOrderApi.getList(params);
      if (res && (res.success || res.ok)) {
        const listData = res.data?.list || res.data?.rows || (Array.isArray(res.data) ? res.data : []);
        const totalCount = res.data?.total ?? res.total ?? listData.length;
        setList(listData);
        setTotal(totalCount);
      } else {
        setList([]);
        setTotal(0);
      }
    } catch (err) {
      console.error('[WorkOrderListPage] 목록 조회 실패:', err);
      setList([]);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, selectedStatus, completedSubTab, searchParams, effectiveAdminAll, selectedYear, location.search]);

  // 3개 탭(지시받은 / 지시한 / 참조) 실시간 전체 건수 및 상태별 건수 단일 초경량 조회 (최적화 완료)
  const fetchTabCounts = useCallback(async () => {
    try {
      const baseParams = {
        ...(effectiveAdminAll ? { adminAll: true } : {}),
        ...(selectedYear && selectedYear !== 'all' ? { year: selectedYear } : {})
      };
      const matrixRes = await workOrderApi.getMatrixCounts(baseParams);
      if (matrixRes && (matrixRes.success || matrixRes.data)) {
        const matrixData = matrixRes.data || matrixRes;
        setStatusCounts(matrixData);
        setTabCounts({
          received: matrixData.received?.total ?? 0,
          sent: matrixData.sent?.total ?? 0,
          referred: matrixData.referred?.total ?? matrixData.cc?.total ?? 0,
        });
      }
    } catch (e) {
      console.error('[WorkOrderListPage] 탭 건수 조회 실패:', e);
    }
  }, [effectiveAdminAll, selectedYear]);

  // 완료된 업무 4개 서브 탭 실시간 전체 건수 병렬 조회
  const fetchCompletedCounts = useCallback(async () => {
    try {
      const baseParams = {
        ...(effectiveAdminAll ? { adminAll: true } : {}),
        ...(selectedYear && selectedYear !== 'all' ? { year: selectedYear } : {})
      };
      const [allRes, recRes, sentRes, refRes] = await Promise.all([
        workOrderApi.getList({ type: 'completed', limit: 1, ...baseParams }),
        workOrderApi.getList({ type: 'completed', status: 'received', limit: 1, ...baseParams }),
        workOrderApi.getList({ type: 'completed', status: 'sent', limit: 1, ...baseParams }),
        workOrderApi.getList({ type: 'completed', status: 'referred', limit: 1, ...baseParams }),
      ]);
      setCompletedCounts({
        all: allRes.data?.total ?? allRes.total ?? 0,
        received: recRes.data?.total ?? recRes.total ?? 0,
        sent: sentRes.data?.total ?? sentRes.total ?? 0,
        referred: refRes.data?.total ?? refRes.total ?? 0,
      });
    } catch (e) {
      console.error('[WorkOrderListPage] 완료 탭 건수 조회 실패:', e);
    }
  }, [effectiveAdminAll, selectedYear]);

  const handleSaved = useCallback(() => {
    closeModal();
    fetchList();
    if (tab === 'completed') fetchCompletedCounts();
    else fetchTabCounts();
  }, [closeModal, fetchList, fetchTabCounts, fetchCompletedCounts, tab]);

  const handleDeleted = useCallback(() => {
    closeModal();
    fetchList();
    if (tab === 'completed') fetchCompletedCounts();
    else fetchTabCounts();
  }, [closeModal, fetchList, fetchTabCounts, fetchCompletedCounts, tab]);

  // 상태 선택 변경 핸들러
  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
    setPage(1);
    if (newStatus === 'action_required') {
      setExpandedRows({});
    }
    const urlParams = new URLSearchParams(location.search);
    if (newStatus && newStatus !== 'all') {
      urlParams.set('status', newStatus);
    } else {
      urlParams.delete('status');
    }
    const newSearch = urlParams.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
  };

  // 완료 탭 서브탭 변경 핸들러
  const handleCompletedSubTabChange = (newSubTab) => {
    setCompletedSubTab(newSubTab);
    setPage(1);
    const urlParams = new URLSearchParams(location.search);
    if (newSubTab && newSubTab !== 'all') {
      urlParams.set('status', newSubTab);
    } else {
      urlParams.delete('status');
    }
    const newSearch = urlParams.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
  };

  // 관리자 전체보기 토글 핸들러 (URL 파라미터 동기화)
  const handleAdminAllToggle = (checked) => {
    setAdminAllChecked(checked);
    setPage(1);
    const urlParams = new URLSearchParams(location.search);
    if (checked) {
      urlParams.set('adminAll', 'true');
    } else {
      urlParams.delete('adminAll');
    }
    const newSearch = urlParams.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
  };

  // URL 쿼리 파라미터(?status=...&adminAll=...) 실시간 연동
  useEffect(() => {
    setPage(1);
    const urlParams = new URLSearchParams(location.search);
    const statusParam = urlParams.get('status') || 'all';
    if (tab === 'completed') {
      setCompletedSubTab(statusParam);
    } else {
      setSelectedStatus(statusParam);
      if (statusParam === 'action_required') {
        setExpandedRows({});
      }
    }
    const adminAllParam = urlParams.get('adminAll');
    if (isRegularAdmin && adminAllParam !== null) {
      setAdminAllChecked(adminAllParam === 'true');
    }
    const yearParam = urlParams.get('year');
    if (yearParam) {
      if (yearParam === 'all') {
        setSelectedYear('all');
      } else {
        const parsedYear = parseInt(yearParam, 10);
        if (!isNaN(parsedYear)) {
          setSelectedYear(parsedYear);
          setYearList(prev => (prev.includes(parsedYear) ? prev : [parsedYear, ...prev]));
        }
      }
    } else {
      setSelectedYear('all');
    }
    setSearchInput('');
    setIsSearching(false);
  }, [tab, location.search, isRegularAdmin]);

  // URL 쿼리 파라미터(?workOrderId=...&tab=...&focusId=...&reportId=...) 상세 모달 및 처리결과 팝업 자동 오픈 연동
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const targetWorkOrderId = urlParams.get('workOrderId') || urlParams.get('detailId');
    const targetTab = urlParams.get('tab');
    const targetFocusId = urlParams.get('focusId');
    const targetReportId = urlParams.get('reportId');

    if (targetReportId) {
      // 1) [처리결과 제출 알림] 처리결과 검토/확인 팝업 즉시 오픈 (사전 데이터 완전 로드)
      const parsedReportId = parseInt(targetReportId, 10);
      const parsedWorkOrderId = targetWorkOrderId ? parseInt(targetWorkOrderId, 10) : null;
      if (!isNaN(parsedReportId)) {
        if (parsedWorkOrderId) {
          workOrderApi.getDetail(parsedWorkOrderId).then(res => {
            if (res && res.data) {
              const rawReport = (res.data.reports || []).find(r => r.id === parsedReportId) || { id: parsedReportId };
              const foundReport = { ...rawReport, work_order: res.data, work_order_id: parsedWorkOrderId };
              setSelectedReport(foundReport);
              setReportParentWorkOrderId(parsedWorkOrderId);
              setReportDetailOpen(true);
            }
          }).catch(() => {
            setSelectedReport({ id: parsedReportId, work_order_id: parsedWorkOrderId });
            setReportParentWorkOrderId(parsedWorkOrderId);
            setReportDetailOpen(true);
          });
        } else {
          setSelectedReport({ id: parsedReportId, work_order_id: parsedWorkOrderId });
          setReportParentWorkOrderId(parsedWorkOrderId);
          setReportDetailOpen(true);
        }
      }
    } else if (targetWorkOrderId) {
      // 2) [업무 상세 알림] 업무 디테일 모달 즉시 오픈
      const parsedId = parseInt(targetWorkOrderId, 10);
      if (!isNaN(parsedId)) {
        setModalWorkOrderId(parsedId);
        setModalType('detail');
        setModalMode('view');
        setModalInitialTab(targetTab || (targetFocusId ? 'feedback' : undefined));
        setModalFocusId(targetFocusId ? parseInt(targetFocusId, 10) : null);
        setModalOpen(true);
      }
    }
  }, [location.search, location.state]);

  useEffect(() => {
    fetchList();
    if (['received', 'sent', 'referred'].includes(tab)) {
      fetchTabCounts();
    } else if (tab === 'completed') {
      fetchCompletedCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchList, fetchTabCounts, fetchCompletedCounts]);

  // ── 검색 ──────────────────────────────────────────────────────
  const handleSearch = () => {
    const kw = searchInput.trim();
    if (!kw) return alert('검색어를 입력해주세요.');
    setSearchParams(prev => ({
      ...(prev.status ? { status: prev.status } : {}),
      searchDomain,
      searchType,
      search: kw,
      [searchType]: kw
    }));
    setIsSearching(true);
    setSearchInput('');
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchDomain('order');
    setSearchType('all');
    setSearchParams({});
    setIsSearching(false);
    if (isAdminRole) {
      setAdminAllChecked(false);
    }
    setPage(1);
  };

  // ── 총 페이지 수 ──────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  // 페이지 번호 묶음(10개 단위) 시작/끝 페이지
  const PAGE_BLOCK = 10;
  const blockStart = Math.floor((page - 1) / PAGE_BLOCK) * PAGE_BLOCK + 1;
  const blockEnd = Math.min(blockStart + PAGE_BLOCK - 1, totalPages);

  // ── 렌더 헬퍼 ─────────────────────────────────────────────────
  const formatCreatedAt = (dateString) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return String(dateString).split('T')[0];
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } catch {
      return String(dateString).split('T')[0];
    }
  };

  const renderPriorityBadge = (priority) => {
    const p = PRIORITY_META[priority] || PRIORITY_META.normal;
    if (priority === 'urgent') {
      return <span className="badge bg-danger me-1 px-2" style={{ fontSize: '0.65rem' }}>긴급</span>;
    }
    if (priority === 'high') {
      return <span className="badge me-1 px-2" style={{ fontSize: '0.65rem', backgroundColor: p.color, color: '#fff' }}>높음</span>;
    }
    return null;
  };

  const renderAssignees = (assignees) => {
    if (!assignees || assignees.length === 0) {
      return <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>미지정</span>;
    }

    // 전체 리스트 (포탈 호버 툴팁용)
    const fullList = assignees.map(a => {
      const u = a.user || {};
      const pos = u.org?.position || u.position || a.position || '';
      const dept = u.org?.dept_name || u.dept_name || a.department || '';
      const name = u.name || a.name || u.userid || '-';
      return `${name}${pos ? ' ' + pos : ''}${dept ? ' (' + dept + ')' : ''}`;
    });

    // 로그인 사용자 본인 3중 크로스체크 매칭 (id, userid, name)
    const uid = String(user?.id || '');
    const uuid = String(user?.userid || '');
    const uname = String(user?.name || '');

    const myAssignee = assignees.find(a => {
      const u = a.user || {};
      return (uid && (String(a.user_id) === uid || String(u.id) === uid)) ||
        (uuid && (String(a.user_id) === uuid || String(u.userid) === uuid)) ||
        (uname && (a.name === uname || u.name === uname));
    });
    const repItem = myAssignee || assignees[0] || {};
    const repUser = repItem.user || {};
    const repPos = repUser.org?.position || repUser.position || repItem.position || '';
    const repName = `${repUser.name || repItem.name || repUser.userid || '-'}${repPos ? ' ' + repPos : ''}`;

    const displayText = assignees.length > 1 ? `${repName} 외 ${assignees.length - 1}명` : repName;

    const badgeStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: '#f4f5f7',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.8rem',
      color: '#334155',
      fontWeight: 600,
      maxWidth: '100%',
      overflow: 'hidden'
    };

    if (assignees.length <= 1) {
      return (
        <div style={badgeStyle} title={fullList.join(', ')}>
          <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.85rem' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
        </div>
      );
    }

    return (
      <div
        style={{ display: 'inline-block', maxWidth: '100%', cursor: 'pointer' }}
        onMouseEnter={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setHoveredAttendees({
            list: fullList,
            x: rect.left + rect.width / 2,
            y: rect.top
          });
        }}
        onMouseLeave={() => setHoveredAttendees(null)}
      >
        <div style={badgeStyle}>
          <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.85rem' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
        </div>
      </div>
    );
  };

  const renderAuthor = (author) => {
    if (!author) return <span className="text-muted small">-</span>;
    const pos = author.org?.position || author.position || '';
    const name = `${author.name || author.userid || '-'}${pos ? ' ' + pos : ''}`;
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: '#f4f5f7',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '0.8rem',
          color: '#334155',
          fontWeight: 600,
          maxWidth: '100%',
          overflow: 'hidden'
        }}
        title={author.org?.dept_name ? `${name} (${author.org.dept_name})` : name}
      >
        <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.85rem' }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      </div>
    );
  };

  const renderProgress = (assignees, status = '', reports = []) => {
    const isDone = ['done', 'completed'].includes(status);
    if (isDone) {
      return (
        <div className="d-flex align-items-center gap-1" style={{ minWidth: '80px' }}>
          <div className="progress flex-grow-1" style={{ height: '6px', borderRadius: '4px' }}>
            <div className="progress-bar bg-success" style={{ width: '100%' }} />
          </div>
          <span className="small fw-bold text-success" style={{ fontSize: '0.7rem', minWidth: '28px' }}>
            100%
          </span>
        </div>
      );
    }
    if (!assignees || assignees.length === 0) return <span className="text-muted small">-</span>;

    // 각 담당자의 '승인된 공식 진척도' 계산 (검토대기 중인 미승인 보고서 진척도는 합산 제외)
    const calcAssigneeApprovedProgress = (a) => {
      if (a.status === 'completed') return 100;
      const userReports = (reports || []).filter(r => String(r.user_id) === String(a.user_id));
      const hasFinalApproved = userReports.some(r => r.approval_status === 'approved' && (r.is_final || Number(r.progress_rate) === 100));
      if (hasFinalApproved) return 100;

      const approvedReports = userReports.filter(r => r.approval_status === 'approved');
      if (approvedReports.length > 0) {
        return Math.max(...approvedReports.map(r => Number(r.progress_rate) || 0));
      }
      if (a.status === 'submitted') {
        return 0; // 승인된 이전 보고서가 없으면 0% 반영
      }
      return Number(a.progress_rate) || 0;
    };

    const avg = Math.round(assignees.reduce((s, a) => s + calcAssigneeApprovedProgress(a), 0) / assignees.length);
    const isComplete = avg === 100;
    return (
      <div className="d-flex align-items-center gap-1" style={{ minWidth: '80px' }}>
        <div className="progress flex-grow-1" style={{ height: '6px', borderRadius: '4px' }}>
          <div
            className={`progress-bar ${isComplete ? 'bg-success' : 'bg-primary'}`}
            style={{ width: `${avg}%` }}
          />
        </div>
        <span className={`small fw-bold ${isComplete ? 'text-success' : 'text-primary'}`}
          style={{ fontSize: '0.7rem', minWidth: '28px' }}>
          {avg}%
        </span>
      </div>
    );
  };

  // ── 상태 뱃지 렌더링 ─────────────────────────────────────────────
  const STATUS_BADGE_MAP = {
    request: { label: '대기', bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
    pending: { label: '대기', bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
    progress: { label: '진행중', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    review: { label: '검토대기', bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
    delay_request: { label: '접수지연', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    delay_progress: { label: '진행지연', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    delay_mixed: { label: '접수·진행지연', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    delay: { label: '지연', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    done: { label: '완료', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    stop: { label: '중단', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
    canceled: { label: '취소됨', bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
    deleted: { label: '삭제됨', bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
    cancel: { label: '취소됨', bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  };

  const renderStatusBadge = (item) => {
    if (!item) return <span className="text-muted small">-</span>;

    // 담당자별 진행 현황(완료 vs 진행중 vs 미접수) 정밀 분류 (복수 담당자 팝오버 및 지연 공통 활용)
    const assignees = item.assignees || [];
    const isJoint = item.completion_type === 'joint' || item.completion_type === 'collective';

    const formatName = (a) => {
      const u = a.user || {};
      const pos = u.org?.position || u.position || a.position || '';
      const name = u.name || a.name || u.userid || '-';
      return `${name}${pos ? ' ' + pos : ''}`.trim();
    };

    // 1. 완료자: status가 completed/done이거나, 승인된 최종 보고서가 있는 경우
    const dones = assignees.filter(a => {
      return ['done', 'completed'].includes(a.status) || (item.reports || []).some(r => String(r.user_id) === String(a.user_id) && r.approval_status === 'approved' && (r.is_final || Number(r.progress_rate) === 100));
    }).map(formatName);

    // 2. 진행중: 완료는 아니지만, 보고서를 1건이라도 제출/승인받았거나 status가 progress/review/submitted인 경우
    const progresses = assignees.filter(a => {
      const isDone = ['done', 'completed'].includes(a.status) || (item.reports || []).some(r => String(r.user_id) === String(a.user_id) && r.approval_status === 'approved' && (r.is_final || Number(r.progress_rate) === 100));
      if (isDone) return false;
      const hasReport = (item.reports || []).some(r => String(r.user_id) === String(a.user_id));
      return hasReport || a.status === 'progress' || a.status === 'review' || a.status === 'submitted';
    }).map(formatName);

    // 3. 미접수: 보고서가 전혀 없고 status가 pending인 경우
    const pendings = assignees.filter(a => {
      const isDone = ['done', 'completed'].includes(a.status) || (item.reports || []).some(r => String(r.user_id) === String(a.user_id) && r.approval_status === 'approved' && (r.is_final || Number(r.progress_rate) === 100));
      if (isDone) return false;
      const hasReport = (item.reports || []).some(r => String(r.user_id) === String(a.user_id));
      return !hasReport && a.status === 'pending';
    }).map(formatName);

    // 복수 담당자(2명 이상)일 때 팝오버 상세 현황 구성 (임시저장/삭제 제외 모든 상태에서 공통 활용)
    let commonMemberInfo = null;
    if (assignees.length >= 2 && !item.deleted_at && !['temp', 'draft'].includes(item.status)) {
      const total = assignees.length;
      const doneCount = dones.length;
      const ongoingCount = progresses.length;
      const pendingCount = pendings.length;

      let summaryText = `총 ${total}명의 담당자 중 `;
      const parts = [];
      if (doneCount > 0) parts.push(`완료 ${doneCount}명`);
      if (ongoingCount > 0) parts.push(`진행중 ${ongoingCount}명`);
      if (pendingCount > 0) parts.push(`미접수 ${pendingCount}명`);
      summaryText += (parts.length > 0 ? parts.join(', ') : '진행중') + '입니다.';

      commonMemberInfo = {
        type: 'members',
        message: summaryText,
        dones,
        progresses,
        pendings
      };
    }

    const renderSimpleBadge = (lbl, bg, clr, bdr, badgeBgColor, desc, memberInfo = null) => (
      <div
        style={{ display: 'inline-block', cursor: 'pointer' }}
        onMouseEnter={(e) => {
          e.stopPropagation();
          if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
          const rect = e.currentTarget.getBoundingClientRect();
          const popoverWidth = 290;
          const spaceLeft = rect.left;
          const spaceRight = window.innerWidth - rect.right;
          const showRight = spaceLeft < popoverWidth && spaceRight >= spaceLeft;

          setHoveredStatus({
            category: '협조',
            label: lbl,
            badgeBg: badgeBgColor || clr,
            message: desc,
            memberInfo,
            x: showRight ? (rect.right + 8) : (rect.left - 8),
            y: rect.top + rect.height / 2,
            badgeY: rect.top + rect.height / 2,
            showRight
          });
        }}
        onMouseLeave={() => {
          hoverStatusTimerRef.current = setTimeout(() => {
            setHoveredStatus(null);
          }, 180);
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            fontSize: '0.72rem',
            fontWeight: 600,
            borderRadius: '12px',
            backgroundColor: bg,
            color: clr,
            border: `1px solid ${bdr}`,
            whiteSpace: 'nowrap'
          }}
        >
          {lbl}
        </span>
      </div>
    );

    if (item.deleted_at) {
      return renderSimpleBadge('삭제됨', '#fef2f2', '#dc2626', '#fecaca', '#dc2626', '삭제 처리된 업무협조입니다.');
    }
    if (item.status === 'temp' || item.status === 'draft') {
      return renderSimpleBadge('임시저장', '#f1f5f9', '#475569', '#e2e8f0', '#64748b', '작성 중 임시 저장된 업무협조입니다.');
    }
    if (item.status === 'canceled' || item.status === 'cancel') {
      return renderSimpleBadge('취소됨', '#fff7ed', '#ea580c', '#fed7aa', '#ea580c', '요청자에 의해 취소된 업무협조 건입니다.', commonMemberInfo);
    }
    if (item.status === 'stop') {
      return renderSimpleBadge('중단', '#fff7ed', '#c2410c', '#fed7aa', '#c2410c', '요청자에 의해 일시 중단된 협조 업무입니다.', commonMemberInfo);
    }

    const status = item.status;
    const dueDateStr = item.due_date;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = dueDateStr ? new Date(dueDateStr) : null;
    if (due) due.setHours(0, 0, 0, 0);
    const diff = due ? Math.round((due - today) / (1000 * 60 * 60 * 24)) : 0;
    const isDelayed = !['done', 'completed', 'stop', 'cancel', 'canceled'].includes(status) && !item.deleted_at && due && diff < 0;

    if (status === 'done' || status === 'completed') {
      return renderSimpleBadge('완료', '#f0fdf4', '#15803d', '#bbf7d0', '#15803d', '최종 완료 종결된 업무입니다.', commonMemberInfo);
    }

    if (!isDelayed) {
      if (diff === 0) {
        let displayLabel = '오늘마감';
        if (assignees.length >= 2) {
          const total = assignees.length;
          const doneCount = dones.length;
          if (doneCount > 0 && doneCount < total) {
            displayLabel = `오늘마감 (${doneCount}/${total})`;
          }
        }
        return renderSimpleBadge(displayLabel, '#fef3c7', '#b45309', '#fde68a', '#b45309', '오늘 마감 예정인 업무입니다.', commonMemberInfo);
      }
      if (diff > 0 && diff <= 2) {
        let displayLabel = '기한임박';
        if (assignees.length >= 2) {
          const total = assignees.length;
          const doneCount = dones.length;
          if (doneCount > 0 && doneCount < total) {
            displayLabel = `기한임박 (${doneCount}/${total})`;
          }
        }
        return renderSimpleBadge(displayLabel, '#fff7ed', '#c2410c', '#ffedd5', '#c2410c', `처리 기한이 D-${diff}일 남은 업무협조입니다.`, commonMemberInfo);
      }

      let targetKey = status || 'request';
      let tooltip = '';
      if (status === 'request' || status === 'pending') tooltip = '담당자의 접수를 대기 중인 업무입니다.';
      else if (status === 'progress') tooltip = '담당자가 정상 일정으로 진행 중인 업무입니다.';
      else if (status === 'review') tooltip = '처리결과가 제출되어 검토 대기 중인 업무입니다.';

      const conf = STATUS_BADGE_MAP[targetKey] || { label: status || '대기', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };

      let displayLabel = conf.label;
      if (assignees.length >= 2) {
        const total = assignees.length;
        const doneCount = dones.length;
        const ongoingCount = progresses.length;

        if (doneCount > 0 && doneCount < total) {
          displayLabel = `진행중 (${doneCount}/${total})`;
        } else if (doneCount === 0 && ongoingCount > 0 && pendings.length > 0) {
          displayLabel = `진행중 (${ongoingCount}/${total})`;
        }
      }

      return renderSimpleBadge(displayLabel, conf.bg, conf.color, conf.border, conf.color, tooltip, commonMemberInfo);
    }

    // 지연 상태일 때
    let label = '진행지연';
    let delayType = 'progress';

    if (assignees.length === 0) {
      label = item.status === 'request' ? '접수지연' : '진행지연';
      delayType = item.status === 'request' ? 'request' : 'progress';
    } else if (isJoint) {
      const hasAccepted = assignees.some(a => a.status !== 'pending');
      if (!hasAccepted) {
        label = '접수지연';
        delayType = 'request';
      } else {
        label = '진행지연';
        delayType = 'progress';
      }
    } else {
      if (pendings.length > 0 && progresses.length > 0) {
        label = '접수·진행지연';
        delayType = 'mixed';
      } else if (pendings.length > 0 && progresses.length === 0) {
        label = '접수지연';
        delayType = 'request';
      } else {
        label = '진행지연';
        delayType = 'progress';
      }
    }

    const delayDaysText = `+${Math.abs(diff)}일 지연`;

    let delayDesc = '';
    if (delayType === 'request') {
      delayDesc = '마감일이 지났으나 담당자가 아직 업무를 접수(시작)하지 않은 상태입니다.';
    } else if (delayType === 'progress') {
      delayDesc = '마감일이 지났으나 처리결과가 완료되지 않고 여전히 진행 중인 상태입니다.';
    } else if (delayType === 'mixed') {
      delayDesc = '마감일이 지난 상태에서 미접수자와 진행 중인 담당자가 함께 존재하는 상태입니다.';
    }

    const delayInfo = {
      type: 'delay',
      delayType,
      title: `${label} (${delayDaysText})`,
      message: delayDesc,
      pendings,
      progresses,
      dones
    };

    return (
      <div
        style={{ display: 'inline-block', cursor: 'pointer' }}
        onMouseEnter={(e) => {
          e.stopPropagation();
          if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
          const rect = e.currentTarget.getBoundingClientRect();
          const popoverWidth = 290;
          const spaceLeft = rect.left;
          const spaceRight = window.innerWidth - rect.right;
          const showRight = spaceLeft < popoverWidth && spaceRight >= spaceLeft;

          setHoveredStatus({
            category: '협조',
            label,
            delayInfo,
            x: showRight ? (rect.right + 8) : (rect.left - 8),
            y: rect.top + rect.height / 2,
            badgeY: rect.top + rect.height / 2,
            showRight
          });
        }}
        onMouseLeave={() => {
          hoverStatusTimerRef.current = setTimeout(() => {
            setHoveredStatus(null);
          }, 180);
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            fontSize: '0.72rem',
            fontWeight: 600,
            borderRadius: '12px',
            backgroundColor: '#fef2f2',
            color: '#b91c1c',
            border: '1px solid #fecaca',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </span>
      </div>
    );
  };

  // 역할별 빠른 액션 버튼 (옵션 1: 소프트 솔리드 / 연한 채움형)
  const renderActions = (item) => {
    const uid = String(user?.id || '');
    const uuid = String(user?.userid || '');
    const uname = String(user?.name || '');

    // 로그인한 사용자 본인의 담당자 배정 레코드 찾기
    const myAssignee = (item.assignees || []).find(a => {
      const u = a.user || {};
      return (uid && (String(a.user_id) === uid || String(u.id) === uid)) ||
        (uuid && (String(a.user_id) === uuid || String(u.userid) === uuid)) ||
        (uname && (a.name === uname || u.name === uname));
    });

    const isAssignee = Boolean(myAssignee);
    const isAuthor = (uid && String(item.author_id) === uid) || (uuid && String(item.author?.userid) === uuid) || (uname && item.author?.name === uname);
    const today = new Date().toISOString().split('T')[0];
    const dueStr = item.due_date ? item.due_date.split('T')[0] : null;
    const isDelayed = item.status !== 'done' && item.status !== 'cancel' && dueStr && dueStr < today;

    const btnStyle = (bg, color, border) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 9px',
      fontSize: '0.74rem',
      fontWeight: 600,
      borderRadius: '4px',
      backgroundColor: bg,
      color: color,
      border: `1px solid ${border}`,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    });

    // 0. 취소/삭제된 업무 전용 액션 (리스트에서는 [재등록]만 제공, [완전삭제]는 상세 조회 팝업에서 수행)
    if (item.deleted_at || item.status === 'canceled' || item.status === 'cancel') {
      return (
        <button
          type="button"
          style={btnStyle('#eff6ff', '#2563eb', '#bfdbfe')}
          onClick={(e) => { e.stopPropagation(); handleRestore(item.id); }}
          title="업무 재등록(복구)"
        >
          <i className="fas fa-undo me-1" />재등록
        </button>
      );
    }

    // 0-0. 임시저장(temp) 전용 액션: 이어작성 버튼
    if (item.status === 'temp' || item.status === 'draft') {
      return (
        <button
          type="button"
          className={isAuthor ? 'wo-pulse-blue' : ''}
          style={btnStyle('#eff6ff', '#2563eb', '#bfdbfe')}
          onClick={(e) => { e.stopPropagation(); openEditModal(item.id); }}
          title="임시저장 업무 이어작성"
        >
          이어작성
        </button>
      );
    }

    // 0-1. 중단(stop)된 업무 전용 액션: 담당자/지시자 모두 오작동(독촉 발송/작성) 방지 및 안전 열람 제공
    if (item.status === 'stop') {
      return (
        <button
          type="button"
          style={btnStyle('#fff7ed', '#c2410c', '#fed7aa')}
          onClick={(e) => { e.stopPropagation(); openViewModal(item.id); }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ffedd5'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff7ed'; }}
          title="중단된 업무 열람"
        >
          열람
        </button>
      );
    }

    // 0-2. 최종 완료(done/completed)된 업무 전용 액션: [열람] 버튼
    if (item.status === 'done' || item.status === 'completed') {
      return (
        <button
          type="button"
          style={btnStyle('#f8fafc', '#475569', '#e2e8f0')}
          onClick={(e) => { e.stopPropagation(); openViewModal(item.id); }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
          title="완료된 업무 열람"
        >
          열람
        </button>
      );
    }

    // 1. 담당자 전용 액션: "내(로그인 사용자) 개인의 진행 상태"를 기준으로 판정!
    if (isAssignee) {
      // 미접수 상태 ➔ [접수] (담당자 본인 시 블루 펄스 깜빡임)
      if (myAssignee.status === 'pending') {
        return (
          <button
            type="button"
            className="wo-pulse-blue"
            style={btnStyle('#eff6ff', '#1d4ed8', '#bfdbfe')}
            onClick={(e) => { e.stopPropagation(); handleAccept(item.id); }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
          >
            접수
          </button>
        );
      }

      // 진행중 상태 ➔ 미종결 보고서(검토대기/보완요청/임시저장)가 있으면 [열람], 신규 작성 가능할 때만 [처리결과 작성]
      if (myAssignee.status === 'progress') {
        const myId = String(user?.id || user?.userid || '');
        const myReports = (item.reports || []).filter(r => {
          const rId = String(r.user_id || r.reporter?.id || r.reporter?.userid || '');
          return rId === myId;
        });

        // 미종결(임시저장, 검토대기, 보완요청) 건이 있으면 신규 차수 작성 불가 ➔ 상위 행은 [열람] 유지
        const hasPendingReport = myReports.some(r =>
          ['draft', 'temp', 'submitted', 'pending', 'review', 'rejected'].includes(r.approval_status)
        );

        if (!hasPendingReport) {
          return (
            <button
              type="button"
              className="wo-pulse-green"
              style={btnStyle('#ecfdf5', '#047857', '#a7f3d0')}
              onClick={(e) => {
                e.stopPropagation();
                setReportFormModalState({ open: true, workOrderId: item.id, workOrder: item });
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#d1fae5'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#ecfdf5'; }}
            >
              처리결과 작성
            </button>
          );
        }
      }


    }

    // 2. 지시자 전용 액션 (지시자 본인일 때만 펄스 깜빡임)
    if (isAuthor) {
      if (item.status === 'review') {
        const pendingReviews = (item.reports || []).filter(r =>
          ['submitted', 'pending', 'review'].includes(r.approval_status)
        );

        return (
          <button
            type="button"
            className="wo-pulse-purple"
            style={btnStyle('#eef2ff', '#4338ca', '#c7d2fe')}
            onClick={(e) => {
              e.stopPropagation();
              // 검토 대기 건이 단 1건이면 곧바로 해당 처리결과 검토 팝업으로 직행
              if (pendingReviews.length === 1) {
                setSelectedReport(pendingReviews[0]);
                setReportParentWorkOrderId(item.id);
                setReportDetailOpen(true);
              } else {
                // 여러 건이거나 목록 선택이 필요한 경우 상세 모달의 보고서 탭 오픈
                openViewModal(item.id, false, 'reports');
              }
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e0e7ff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eef2ff'; }}
            title={pendingReviews.length === 1 ? '처리결과 검토 창을 바로 엽니다.' : '검토할 처리결과 목록을 엽니다.'}
          >
            처리결과 검토
          </button>
        );
      }
      if (isDelayed) {
        return (
          <button
            type="button"
            className="wo-pulse-red"
            style={btnStyle('#fef2f2', '#b91c1c', '#fecaca')}
            onClick={(e) => { e.stopPropagation(); handleRemind(item.id); }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
          >
            독촉 발송
          </button>
        );
      }
    }

    // 3. 참조자 또는 추가 액션 없는 상태 (열람 제공)
    return (
      <button
        type="button"
        style={btnStyle('#f8fafc', '#475569', '#e2e8f0')}
        onClick={(e) => { e.stopPropagation(); openViewModal(item.id); }}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
      >
        열람
      </button>
    );
  };

  const handleRestore = async (id) => {
    if (!window.confirm('이 업무를 다시 진행 상태로 재등록(복구)하시겠습니까?')) return;
    try {
      await workOrderApi.restore(id);
      alert('업무가 정상적으로 재등록되었습니다.');
      fetchList();
    } catch (err) {
      alert('재등록 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleHardDelete = async (id) => {
    if (!window.confirm('정말 이 업무를 DB에서 완전히 삭제하시겠습니까?\n삭제 후에는 영구히 복구할 수 없습니다.')) return;
    try {
      await workOrderApi.hardDelete(id);
      alert('업무가 완전히 삭제되었습니다.');
      fetchList();
    } catch (err) {
      alert('완전 삭제 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAccept = async (id) => {
    if (!window.confirm('업무를 접수(시작)하시겠습니까?')) return;
    try {
      await workOrderApi.changeAction(id, { action: 'accept' });
      fetchList();
    } catch (err) {
      alert('접수 처리 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleRemind = async (id) => {
    if (!window.confirm('담당자에게 독촉 알림을 발송하시겠습니까?')) return;
    try {
      await workOrderApi.sendReminder(id);
      alert('독촉 알림이 발송되었습니다.');
    } catch (err) {
      alert('알림 발송 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── 연도 선택 드롭다운 공통 렌더 헬퍼 ──
  const renderYearDropdown = () => (
    <div className="position-relative" ref={yearDropdownRef} style={{ display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setIsYearOpen(prev => !prev)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '7px',
          height: '38px',
          padding: '0 14px',
          border: '1px solid #cbd5e1',
          borderRadius: '10px',
          backgroundColor: '#f8fafc',
          color: '#1e293b',
          fontWeight: 700,
          fontSize: '0.88rem',
          cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.18s ease'
        }}
        title="조회 연도 선택"
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
      >
        <i className="far fa-calendar-alt" style={{ fontSize: '0.9rem', color: '#2563eb' }} />
        <span>{selectedYear === 'all' ? '전체 연도' : `${selectedYear}년`}</span>
        <i className="fas fa-chevron-down" style={{ fontSize: '0.72rem', color: '#64748b', marginLeft: '2px' }} />
      </button>

      {isYearOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 5px)',
            left: 0,
            zIndex: 1050,
            minWidth: '185px',
            backgroundColor: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '10px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            padding: '6px',
            display: 'flex',
            flexDirection: 'column',
            gap: '3px'
          }}
        >
          {/* 팝오버 상단 은은한 미니 헤더 바 */}
          <div
            style={{
              padding: '3px 8px 5px 8px',
              borderBottom: '1px solid #f1f5f9',
              marginBottom: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', letterSpacing: '-0.2px' }}>
              <i className="far fa-calendar-check me-1" style={{ color: '#2563eb' }} />
              조회 연도
            </span>
          </div>

          {/* 1. 최상단: 전체 연도 옵션 */}
          <button
            type="button"
            onClick={() => {
              setSelectedYear('all');
              setIsYearOpen(false);
              setPage(1);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '7px 10px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: selectedYear === 'all' ? '#eff6ff' : 'transparent',
              color: selectedYear === 'all' ? '#1d4ed8' : '#1e293b',
              fontWeight: selectedYear === 'all' ? 700 : 600,
              fontSize: '0.86rem',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={e => {
              if (selectedYear !== 'all') e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={e => {
              if (selectedYear !== 'all') e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span className="d-flex align-items-center gap-1.5">
              <i className="fas fa-layer-group" style={{ fontSize: '0.8rem', color: selectedYear === 'all' ? '#2563eb' : '#64748b' }} />
              전체 연도
            </span>
            {selectedYear === 'all' && <i className="fas fa-check" style={{ fontSize: '0.75rem', color: '#2563eb' }} />}
          </button>

          {/* 전체 연도 구분선 (밑줄) */}
          <div style={{ borderBottom: '1px solid #e2e8f0', margin: '3px 0 2px 0' }} />

          {/* 2. 연도 목록 (최신순이 맨 위) */}
          <div style={{ maxHeight: '190px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px', padding: '1px 0' }}>
            {yearList.map(y => {
              const isSelected = selectedYear === y;
              return (
                <button
                  key={y}
                  type="button"
                  onClick={() => {
                    setSelectedYear(y);
                    setIsYearOpen(false);
                    setPage(1);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                    color: isSelected ? '#1d4ed8' : '#334155',
                    fontWeight: isSelected ? 700 : 500,
                    fontSize: '0.86rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f1f5f9';
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span>{y}년</span>
                  {isSelected && <i className="fas fa-check" style={{ fontSize: '0.75rem', color: '#2563eb' }} />}
                </button>
              );
            })}
          </div>

          {/* 하단 세련된 3단 컨트롤 툴바 (1:1:1 균등 3등분: [이전 3년] [ ↻ ] [축소]) */}
          <div
            style={{
              borderTop: '1px solid #e2e8f0',
              paddingTop: '6px',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {/* 이전 3년 (과거 3개년 추가) */}
            <button
              type="button"
              onClick={handleAddPastYears}
              title="과거 3개년 불러오기"
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '28px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                padding: '0 2px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <span>이전 3년</span>
            </button>

            {/* 초기화 (1:1:1 균등 너비) */}
            {(() => {
              const isResetDisabled = selectedYear === 'all' && yearList.length <= 4;
              return (
                <button
                  type="button"
                  onClick={handleResetYears}
                  disabled={isResetDisabled}
                  title={isResetDisabled ? '이미 기본 연도 상태입니다' : '기본 연도로 초기화'}
                  style={{
                    flex: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '28px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isResetDisabled ? '#f8fafc' : '#f1f5f9',
                    color: isResetDisabled ? '#cbd5e1' : '#475569',
                    fontSize: '0.78rem',
                    cursor: isResetDisabled ? 'not-allowed' : 'pointer',
                    opacity: isResetDisabled ? 0.6 : 1,
                    transition: 'all 0.15s ease',
                    padding: '0 2px'
                  }}
                  onMouseEnter={e => {
                    if (!isResetDisabled) {
                      e.currentTarget.style.backgroundColor = '#e2e8f0';
                      e.currentTarget.style.color = '#1e293b';
                      e.currentTarget.style.transform = 'rotate(-45deg)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isResetDisabled) {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                      e.currentTarget.style.color = '#475569';
                      e.currentTarget.style.transform = 'none';
                    }
                  }}
                >
                  <i className="fas fa-undo-alt" />
                </button>
              );
            })()}

            {/* 축소 (과거 연도 접기) */}
            <button
              type="button"
              onClick={handleRemovePastYears}
              disabled={yearList.length <= 4}
              title={yearList.length <= 4 ? '기본 4개년은 유지됩니다' : '과거 연도 축소'}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '28px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: yearList.length <= 4 ? '#f8fafc' : '#fff1f2',
                color: yearList.length <= 4 ? '#cbd5e1' : '#e11d48',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: yearList.length <= 4 ? 'not-allowed' : 'pointer',
                opacity: yearList.length <= 4 ? 0.7 : 1,
                transition: 'all 0.15s ease',
                padding: '0 2px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={e => {
                if (yearList.length > 4) {
                  e.currentTarget.style.backgroundColor = '#ffe4e6';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={e => {
                if (yearList.length > 4) {
                  e.currentTarget.style.backgroundColor = '#fff1f2';
                  e.currentTarget.style.transform = 'none';
                }
              }}
            >
              <span>축소</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── 상태 메타 (색상/스타일) ──
  const STATUS_BADGE_META = {
    all: { dotColor: '#64748b', bg: '#f1f5f9', color: '#475569' },
    stop: { dotColor: '#ea580c', bg: '#fff7ed', color: '#c2410c' },
    request: { dotColor: '#f59e0b', bg: '#fffbeb', color: '#b45309' },
    progress: { dotColor: '#2563eb', bg: '#eff6ff', color: '#1d4ed8' },
    review: { dotColor: '#8b5cf6', bg: '#f5f3ff', color: '#6d28d9' },
    delay: { dotColor: '#ef4444', bg: '#fef2f2', color: '#b91c1c' },
  };

  // ── 상태 선택 커스텀 드롭다운 렌더 헬퍼 (연도 캡슐 버튼 스타일 일체화 & 실시간 개수 표시) ──
  const renderStatusDropdown = () => {
    const options = STATUS_OPTIONS_BY_TAB[tab];
    if (!options) return null;

    // 현재 선택된 상태 객체
    const activeOpt = options.find(o => o.value === selectedStatus) || options[0];
    const isFiltered = selectedStatus !== 'all' && selectedStatus !== 'action_required';

    // 현재 탭의 상태별 건수 객체 (Matrix API 데이터 기반)
    const currentTabCounts = statusCounts[tab] || {};
    const getCountForStatus = (val) => {
      if (val === 'all') {
        return currentTabCounts.total ?? tabCounts[tab] ?? total ?? 0;
      }
      return currentTabCounts[val] ?? 0;
    };

    const currentCount = getCountForStatus(activeOpt.value);
    const activeMeta = STATUS_BADGE_META[activeOpt.value] || STATUS_BADGE_META.all;

    return (
      <div className="position-relative" ref={statusDropdownRef} style={{ display: 'inline-block' }}>
        <button
          type="button"
          onClick={() => setIsStatusOpen(prev => !prev)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            height: '38px',
            padding: '0 14px',
            border: `1px solid ${isFiltered ? '#bfdbfe' : '#cbd5e1'}`,
            borderRadius: '10px',
            backgroundColor: isFiltered ? '#eff6ff' : '#ffffff',
            color: isFiltered ? '#1d4ed8' : '#334155',
            fontWeight: 700,
            fontSize: '0.85rem',
            cursor: 'pointer',
            boxShadow: isFiltered ? '0 1px 3px rgba(37, 99, 235, 0.12)' : '0 1px 3px rgba(0, 0, 0, 0.06)',
            transition: 'all 0.18s ease',
            whiteSpace: 'nowrap',
            boxSizing: 'border-box'
          }}
          title="업무 상태 필터링"
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = isFiltered ? '#dbeafe' : '#f8fafc';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = isFiltered ? '#eff6ff' : '#ffffff';
          }}
        >
          {isFiltered ? (
            <span style={{ color: activeMeta.dotColor, fontSize: '0.75rem', lineHeight: 1 }}>●</span>
          ) : (
            <i className="fas fa-filter" style={{ fontSize: '0.78rem', color: '#64748b' }} />
          )}
          <span>{activeOpt.label}</span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1px 7px',
              borderRadius: '10px',
              fontSize: '0.74rem',
              fontWeight: 700,
              backgroundColor: isFiltered ? '#dbeafe' : '#f1f5f9',
              color: isFiltered ? '#1e40af' : '#475569',
              lineHeight: 1.2,
              minWidth: '20px'
            }}
          >
            {currentCount}
          </span>
          <i className="fas fa-chevron-down" style={{ fontSize: '0.7rem', color: isFiltered ? '#3b82f6' : '#64748b', marginLeft: '1px' }} />
        </button>

        {isStatusOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 5px)',
              right: 0,
              zIndex: 1050,
              minWidth: '185px',
              backgroundColor: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '10px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              padding: '6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}
          >
            {/* 팝오버 상단 미니 헤더 */}
            <div
              style={{
                padding: '4px 8px 6px 8px',
                borderBottom: '1px solid #f1f5f9',
                marginBottom: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', letterSpacing: '-0.2px' }}>
                <i className="fas fa-sliders-h me-1" style={{ color: '#2563eb' }} />
                상태별 필터
              </span>
            </div>

            {/* 상태 목록 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {options.map(opt => {
                const isSelected = selectedStatus === opt.value || (selectedStatus === 'action_required' && opt.value === 'all');
                const optCount = getCountForStatus(opt.value);
                const metaItem = STATUS_BADGE_META[opt.value] || STATUS_BADGE_META.all;

                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      handleStatusChange(opt.value);
                      setIsStatusOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '7px 10px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                      color: isSelected ? '#1d4ed8' : '#334155',
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: '0.84rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      {opt.value !== 'all' && (
                        <span style={{ color: metaItem.dotColor, fontSize: '0.7rem' }}>●</span>
                      )}
                      <span>{opt.label}</span>
                      {isSelected && (
                        <i className="fas fa-check" style={{ fontSize: '0.72rem', color: '#2563eb', marginLeft: '2px' }} />
                      )}
                    </span>

                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1px 7px',
                        borderRadius: '10px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        backgroundColor: isSelected ? '#dbeafe' : metaItem.bg,
                        color: isSelected ? '#1e40af' : metaItem.color,
                        lineHeight: 1.2,
                        minWidth: '20px'
                      }}
                    >
                      {optCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── 렌더 ──────────────────────────────────────────────────────
  return (
    <div className="container-fluid px-0" style={{ marginTop: '-1.25rem' }}>

      {/* ── 1. 상단 독립 헤더 바 (프리미엄 딥 다크 네이비 카드) ── */}
      <div
        className="card mb-3 border-0"
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: '16px',
          boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.22)',
          color: '#ffffff'
        }}
      >
        <div className="card-body d-flex justify-content-between align-items-center py-3.5 px-4">
          <div className="d-flex align-items-center">
            <div
              className="d-flex justify-content-center align-items-center me-3 flex-shrink-0"
              style={{
                width: '46px',
                height: '46px',
                borderRadius: '14px',
                background: 'rgba(59, 130, 246, 0.15)',
                border: '1px solid rgba(96, 165, 250, 0.3)',
                color: '#60a5fa'
              }}
            >
              <i className={`fas ${meta.icon} fs-5`} />
            </div>
            <div>
              <div className="d-flex align-items-center mb-1">
                <h4 className="mb-0 fw-bold text-white d-flex align-items-center" style={{ letterSpacing: '-0.02em', gap: '12px' }}>
                  {['received', 'sent', 'referred'].includes(tab) ? (
                    <span className="d-inline-flex align-items-center" style={{ gap: '8px' }}>
                      <span className="text-white fw-bold">진행중 업무</span>
                      <i className="fas fa-chevron-right" style={{ fontSize: '0.75rem', color: '#64748b' }} />
                      <span style={{ color: '#93c5fd', fontSize: '1.05rem', fontWeight: 600 }}>{meta.title}</span>
                    </span>
                  ) : tab === 'completed' ? (
                    <span className="d-inline-flex align-items-center" style={{ gap: '8px' }}>
                      <span className="text-white fw-bold">완료된 업무</span>
                      <i className="fas fa-chevron-right" style={{ fontSize: '0.75rem', color: '#64748b' }} />
                      <span style={{ color: '#93c5fd', fontSize: '1.05rem', fontWeight: 600 }}>
                        {COMPLETED_SUB_TABS.find(t => t.key === (completedSubTab || 'all'))?.label || '전체'}
                      </span>
                    </span>
                  ) : (
                    <span>{meta.title}</span>
                  )}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '24px',
                      padding: '0 10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.18)',
                      borderRadius: '12px',
                      fontSize: '0.74rem',
                      color: '#93c5fd',
                      fontWeight: 600,
                      gap: '5px'
                    }}
                  >
                    <i className="fas fa-layer-group" style={{ color: '#60a5fa', fontSize: '0.72rem' }} />
                    <span>총 {total}건</span>
                  </span>
                </h4>
              </div>
              <span className="fw-medium" style={{ fontSize: '0.84rem', color: '#94a3b8' }}>
                {meta.desc}
              </span>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-sm fw-bold d-flex align-items-center gap-1.5"
              onClick={fetchList}
              style={{
                height: '38px',
                padding: '0 14px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: '#e2e8f0'
              }}
            >
              <i className="fas fa-sync-alt" style={{ fontSize: '0.8rem', color: '#93c5fd' }} /> 새로고침
            </button>
            <button
              className="btn btn-sm fw-bold d-flex align-items-center gap-1.5 text-white"
              onClick={() => navigate('/work-order/matrix')}
              style={{
                height: '38px',
                padding: '0 16px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'none',
                borderRadius: '10px',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)'
              }}
            >
              <i className="fas fa-th-large" /> 업무협조 종합 현황
            </button>
          </div>
        </div>
      </div>

      {/* ── 2. 본문 대시보드 곡선 캔버스 패널 (업무 Matrix 현황과 100% 동일 규격 및 20px 24px 여백) ── */}
      <div
        className="rounded-4 mb-0"
        style={{
          backgroundColor: '#f4f5f7',
          border: '1px solid #cbd5e1',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
          padding: '20px 24px',
          marginBottom: '0px',
          minHeight: '690px'
        }}
      >
        {/* ── 3개 진행중 업무 탭 바 (토스/Linear 스타일 슬릭 세그먼트 스위처 + 참조 업무 바로 옆 상태 선택기) ── */}
        {['received', 'sent', 'referred'].includes(tab) && (
          <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
            {renderYearDropdown()}
            {/* ── 3개 진행중 업무 탭 바 (원본 깔끔한 슬라이드 캡슐 완전 복원) ── */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: '#e2e8f0',
                borderRadius: '12px',
                padding: '4px',
                gap: '4px',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.06)'
              }}
            >
              {[
                { key: 'received', label: '요청받은 업무', path: '/work-order/received', icon: 'fa-inbox' },
                { key: 'sent', label: '요청한 업무', path: '/work-order/sent', icon: 'fa-paper-plane' },
                { key: 'referred', label: '참조 업무', path: '/work-order/referred', icon: 'fa-eye' },
              ].map(t => {
                const isActive = tab === t.key;
                const countVal = tabCounts[t.key] ?? 0;
                return (
                  <button
                    key={t.key}
                    type="button"
                    className="border-0"
                    onClick={() => {
                      if (tab !== t.key) {
                        navigate(t.path);
                      }
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: '38px',
                      padding: '0 16px',
                      border: '0',
                      outline: 'none',
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#ffffff' : 'transparent',
                      color: isActive ? '#1d4ed8' : '#64748b',
                      fontWeight: isActive ? 700 : 600,
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      boxShadow: isActive ? '0 2px 6px -1px rgba(0, 0, 0, 0.12), 0 2px 4px -2px rgba(0, 0, 0, 0.06)' : 'none',
                      transition: 'all 0.18s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.6)';
                        e.currentTarget.style.color = '#1e293b';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#64748b';
                      }
                    }}
                  >
                    <i className={`fas ${t.icon}`} style={{ color: isActive ? '#2563eb' : '#94a3b8', fontSize: '0.85rem' }} />
                    <span>{t.label}</span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1px 7px',
                        borderRadius: '10px',
                        fontSize: '0.74rem',
                        fontWeight: 700,
                        backgroundColor: isActive ? '#dbeafe' : 'rgba(0, 0, 0, 0.08)',
                        color: isActive ? '#1d4ed8' : '#475569',
                        lineHeight: 1.2,
                        minWidth: '20px'
                      }}
                    >
                      {countVal}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── 우측 필터 영역: 상태 드롭다운 + ⚡ 조치 필요 체크박스 + 관리자 전체보기 ── */}
            <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
              {STATUS_OPTIONS_BY_TAB[tab] && (
                <>
                  {/* 상태 선택 커스텀 드롭다운 */}
                  {renderStatusDropdown()}

                  {/* ⚡ 조치 필요 체크박스 (오직 지시받은 업무와 지시한 업무에서만 노출, 참조 업무는 미노출) */}
                  {(tab === 'received' || tab === 'sent') && (
                    <label
                      className="d-inline-flex align-items-center gap-2 m-0"
                      style={{
                        height: '38px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        userSelect: 'none',
                        backgroundColor: selectedStatus === 'action_required' ? '#fffbeb' : '#ffffff',
                        color: selectedStatus === 'action_required' ? '#b45309' : '#334155',
                        padding: '0 14px',
                        borderRadius: '10px',
                        border: `1px solid ${selectedStatus === 'action_required' ? '#f59e0b' : '#cbd5e1'}`,
                        boxShadow: selectedStatus === 'action_required' ? '0 2px 4px rgba(245, 158, 11, 0.2)' : '0 1px 3px rgba(0,0,0,0.06)',
                        transition: 'all 0.15s ease',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box'
                      }}
                      title="신속한 조치가 필요한 업무만 필터링 (체크 시 필터, 해제 시 전체 초기화)"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={selectedStatus === 'action_required'}
                        onChange={(e) => {
                          handleStatusChange(e.target.checked ? 'action_required' : 'all');
                        }}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: '#f59e0b',
                          marginTop: '-1px'
                        }}
                      />
                      <span className="d-inline-flex align-items-center gap-1" style={{ lineHeight: 1 }}>
                        <i className="fas fa-bolt" style={{ fontSize: '11px', color: '#f59e0b', marginTop: '-1px' }}></i>
                        <span>조치 필요</span>
                      </span>
                    </label>
                  )}
                </>
              )}

              {/* 상단 관리자 전체보기 체크박스 (일반 관리자 계정 전용 노출) */}
              {isRegularAdmin && (
                <label
                  className="d-inline-flex align-items-center gap-2 m-0"
                  style={{
                    height: '38px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#334155',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundColor: '#ffffff',
                    padding: '0 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={adminAllChecked}
                    onChange={(e) => handleAdminAllToggle(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      accentColor: '#2563eb',
                      marginTop: '-1px'
                    }}
                  />
                  <span style={{ lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>관리자 전체보기</span>
                </label>
              )}
            </div>
          </div>
        )}

        {/* ── 완료된 업무 전용 세그먼트 탭 바 (전체 / 지시받은 업무 / 지시내린 업무 / 참조 업무) ── */}
        {tab === 'completed' && (
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              {renderYearDropdown()}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                backgroundColor: '#e2e8f0',
                borderRadius: '12px',
                padding: '4px',
                gap: '4px',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.06)'
              }}
            >
              {COMPLETED_SUB_TABS.map(t => {
                const isActive = (completedSubTab || 'all') === t.key;
                const countVal = completedCounts[t.key] ?? 0;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => handleCompletedSubTabChange(t.key)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: '38px',
                      padding: '0 16px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#ffffff' : 'transparent',
                      color: isActive ? '#059669' : '#64748b',
                      fontWeight: isActive ? 700 : 600,
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      boxShadow: isActive ? '0 2px 6px -1px rgba(0, 0, 0, 0.12), 0 2px 4px -2px rgba(0, 0, 0, 0.06)' : 'none',
                      transition: 'all 0.18s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.6)';
                        e.currentTarget.style.color = '#1e293b';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#64748b';
                      }
                    }}
                  >
                    <i className={`fas ${t.icon}`} style={{ color: isActive ? '#10b981' : '#94a3b8', fontSize: '0.85rem' }} />
                    <span>{t.label}</span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1px 7px',
                        borderRadius: '10px',
                        fontSize: '0.74rem',
                        fontWeight: 700,
                        backgroundColor: isActive ? '#d1fae5' : 'rgba(0, 0, 0, 0.08)',
                        color: isActive ? '#065f46' : '#475569',
                        lineHeight: 1.2,
                        minWidth: '20px'
                      }}
                    >
                      {countVal}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

            {/* 상단 관리자 전체보기 체크박스 (일반 관리자 계정 전용 노출, 최고관리자 admin은 자동 전사 표출로 숨김) */}
            {isRegularAdmin && (
              <label
                className="d-flex align-items-center gap-2 m-0 ms-auto"
                style={{
                  height: '38px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#334155',
                  cursor: 'pointer',
                  userSelect: 'none',
                  backgroundColor: '#ffffff',
                  padding: '0 14px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  whiteSpace: 'nowrap',
                  boxSizing: 'border-box'
                }}
              >
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={adminAllChecked}
                  onChange={(e) => handleAdminAllToggle(e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    accentColor: '#2563eb'
                  }}
                />
                <span>관리자 전체보기</span>
              </label>
            )}
          </div>
        )}


        {/* 패널 내부 테이블 카드 (완전하고 온전한 프리미엄 라운드 복원) */}
        <div
          className="card shadow-sm overflow-hidden bg-white"
          style={{
            border: '1px solid #cbd5e1',
            borderRadius: '12px'
          }}
        >
          {/* 컨트롤 패널: 왼쪽 검색, 오른쪽 새 글 작성 */}
          <div
            className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3"
            style={{ padding: '16px 22px' }}
          >
            {/* 왼쪽: 2단 검색 영역 */}
            <div className="d-flex align-items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
              {/* 1차 드롭다운: 검색 대상 (임시저장 탭에서는 결과보고서가 없으므로 미노출) */}
              {tab !== 'temp' && (
                <select
                  className="form-select form-select-sm fw-bold"
                  value={searchDomain}
                  onChange={e => {
                    const nextDomain = e.target.value;
                    setSearchDomain(nextDomain);
                    setSearchType('all');
                    setSearchInput('');
                  }}
                  style={{
                    width: '115px',
                    height: '38px',
                    border: '1.5px solid #94a3b8',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#1e293b',
                    backgroundColor: '#f8fafc'
                  }}
                >
                  {SEARCH_DOMAIN_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}

              {/* 2차 드롭다운: 검색 항목 (1차 대상에 따라 동적 변경) */}
              <select
                className="form-select form-select-sm"
                value={searchType}
                onChange={e => { setSearchType(e.target.value); setSearchInput(''); }}
                style={{
                  width: '105px',
                  height: '38px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  color: '#475569',
                  backgroundColor: '#fff'
                }}
              >
                {(searchDomain === 'report' ? SEARCH_REPORT_TYPE_OPTIONS : SEARCH_ORDER_TYPE_OPTIONS).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder=""
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                style={{
                  width: '220px',
                  height: '38px',
                  padding: '0 12px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.85rem',
                  backgroundColor: '#fff'
                }}
              />
              <button
                onClick={handleSearch}
                style={{
                  height: '38px',
                  padding: '0 16px',
                  backgroundColor: '#64748b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                검색
              </button>
              <button
                onClick={handleReset}
                style={{
                  height: '38px',
                  padding: '0 16px',
                  backgroundColor: '#ffffff',
                  color: '#64748b',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                초기화
              </button>
              {/* 검색어 뱃지 */}
              {isSearching && searchParams.search && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '16px',
                    fontSize: '0.8rem',
                    color: '#1d4ed8',
                    fontWeight: 600
                  }}
                >
                  <span>[{searchParams.searchDomain === 'report' ? '처리결과' : '업무협조'}] '{searchParams.search}' 검색결과</span>
                  <i
                    className="fas fa-times"
                    style={{ cursor: 'pointer', background: '#dbeafe', borderRadius: '50%', padding: '2px 4px', fontSize: '0.7rem' }}
                    onClick={handleReset}
                  />
                </div>
              )}
            </div>

            {/* 오른쪽: 관리자 전체보기 체크박스 & 업무지시 등록 버튼 */}
            <div className="d-flex align-items-center gap-2">
              {tab === 'reports' && renderYearDropdown()}
              {isRegularAdmin && !['received', 'sent', 'referred', 'completed'].includes(tab) && (
                <label
                  className="d-flex align-items-center gap-2 m-0"
                  style={{
                    height: '38px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#334155',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundColor: '#ffffff',
                    padding: '0 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={adminAllChecked}
                    onChange={(e) => handleAdminAllToggle(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      accentColor: '#2563eb'
                    }}
                  />
                  <span>관리자 전체보기</span>
                </label>
              )}
              <button
                onClick={openCreateModal}
                style={{
                  height: '38px',
                  padding: '0 18px',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-plus me-1" /> 업무협조 요청
              </button>
            </div>
          </div>

          {/* 테이블 */}
          <div className="table-responsive">
            <style>{`
              .work-order-parent-expanded > td {
                border-bottom: 1px solid #e2e8f0 !important;
              }
            `}</style>
            <table className="table table-hover align-middle mb-0" style={{ minWidth: '980px', tableLayout: 'fixed' }}>
              <thead className="table-light">
                <tr style={{ height: '42px', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ width: '55px', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>NO</th>
                  <th style={{ width: 'auto', textAlign: 'left', paddingLeft: '16px', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>
                    <div className="d-flex align-items-center gap-2">
                      <span>제목</span>
                      {hasAnyReports && searchParams.searchDomain !== 'report' && (
                        <button
                          type="button"
                          className="btn btn-sm d-inline-flex align-items-center gap-1 shadow-none"
                          style={{
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            color: isAllExpanded ? '#0d9488' : '#2563eb',
                            backgroundColor: isAllExpanded ? '#f0fdfa' : '#eff6ff',
                            border: `1px solid ${isAllExpanded ? '#99f6e4' : '#bfdbfe'}`,
                            borderRadius: '6px',
                            padding: '2px 8px',
                            height: '24px',
                            lineHeight: 1,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = isAllExpanded ? '#ccfbf1' : '#dbeafe';
                            e.currentTarget.style.borderColor = isAllExpanded ? '#5eead4' : '#93c5fd';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isAllExpanded ? '#f0fdfa' : '#eff6ff';
                            e.currentTarget.style.borderColor = isAllExpanded ? '#99f6e4' : '#bfdbfe';
                          }}
                          onClick={toggleAllReports}
                          title={isAllExpanded ? '모든 처리결과 일괄 접기' : '모든 처리결과 일괄 펼치기'}
                        >
                          <i className={`fas fa-chevron-${isAllExpanded ? 'up' : 'down'}`} style={{ fontSize: '0.68rem' }} />
                          {isAllExpanded ? '처리결과 접기' : '처리결과 펼치기'}
                        </button>
                      )}
                    </div>
                  </th>
                  <th style={{ width: '150px', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>요청자</th>
                  <th style={{ width: '185px', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>담당자</th>
                  <th style={{ width: '95px', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>D-Day</th>
                  <th style={{ width: '100px', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>진척률</th>
                  <th style={{ width: '110px', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>상태</th>
                  <th style={{ width: '100px', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>액션</th>
                  <th style={{ width: '100px', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>요청일</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {isLoading ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '60px 24px', borderBottom: '1px solid #e2e8f0' }}>
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                      <span className="text-secondary small">데이터를 불러오는 중입니다...</span>
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '60px 24px', borderBottom: '1px solid #e2e8f0' }}>
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <div style={{ color: '#cbd5e1', fontSize: '2.5rem', marginBottom: '12px' }}>
                          <i className={isSearching ? 'fas fa-search' : 'fas fa-folder-open'} />
                        </div>
                        <div style={{ fontSize: '0.88rem', color: '#64748b', fontWeight: 500 }}>
                          {isSearching ? (
                            <>
                              <span style={{ color: '#2563eb', fontWeight: 700 }}>[{searchParams.search || searchInput}]</span> 검색 결과가 없습니다.
                            </>
                          ) : (
                            `${meta.title} 목록이 없습니다.`
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  list.map((item, idx) => {
                    const rowNum = (page - 1) * LIMIT + idx + 1;

                    // ── [결과보고서 검색 결과 렌더링: 기존 하위 보고서 행 UI와 100% 동일 규격] ──
                    if (searchParams.searchDomain === 'report') {
                      const parentWO = item.workOrder || {};
                      const matchedAssignee = (parentWO.assignees || []).find(a =>
                        String(a.user_id) === String(item.user_id || item.reporter?.id || item.reporter?.userid)
                      );
                      const repPos = item.reporter?.org?.position || item.reporter?.position || '';
                      const repName = item.reporter?.name || matchedAssignee?.user?.name || '담당자';
                      const reporterDisplayText = `${repName}${repPos ? ' ' + repPos : ''}`;

                      const repProgressRate = item.progress_rate !== undefined && item.progress_rate !== null
                        ? Number(item.progress_rate)
                        : (matchedAssignee?.progress_rate !== undefined && matchedAssignee?.progress_rate !== null ? Number(matchedAssignee.progress_rate) : 0);
                      const isComplete = repProgressRate === 100;
                      const repStatus = item.approval_status || item.status;

                      let conf = {
                        label: '검토대기',
                        bg: '#f5f3ff',
                        color: '#6d28d9',
                        border: '#ddd6fe',
                        badgeBg: '#6d28d9',
                        message: '처리결과가 제출되어 요청자의 검토 및 확인을 기다리는 상태입니다.'
                      };
                      if (repStatus === 'approved' || repStatus === 'completed' || repStatus === 'done') {
                        conf = {
                          label: '확인완료',
                          bg: '#f0fdf4',
                          color: '#15803d',
                          border: '#bbf7d0',
                          badgeBg: '#15803d',
                          message: '요청자가 처리결과를 최종 확인완료한 상태입니다.'
                        };
                      } else if (repStatus === 'rejected' || repStatus === 'reject') {
                        conf = {
                          label: '보완요청',
                          bg: '#fef2f2',
                          color: '#b91c1c',
                          border: '#fecaca',
                          badgeBg: '#b91c1c',
                          message: '요청자가 처리결과 검토 후 보완(수정)을 요청한 상태입니다.'
                        };
                      } else if (repStatus === 'draft' || repStatus === 'temp') {
                        conf = {
                          label: '임시저장',
                          bg: '#f1f5f9',
                          color: '#64748b',
                          border: '#e2e8f0',
                          badgeBg: '#64748b',
                          message: '작성 중 임시저장된 초안으로, 본인만 열람 및 이어작성할 수 있습니다.'
                        };
                      }

                      return (
                        <tr
                          key={`rep-search-${item.id}`}
                          onClick={() => {
                            setSelectedReport(item);
                            setReportParentWorkOrderId(item.work_order_id);
                            setReportDetailOpen(true);
                          }}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; }}
                        >
                          {/* 1열: NO */}
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '28px',
                                height: '24px',
                                padding: '0 6px',
                                backgroundColor: '#f1f5f9',
                                borderRadius: '6px',
                                color: '#64748b',
                                fontWeight: 600,
                                fontSize: '0.8rem'
                              }}
                            >
                              {rowNum}
                            </span>
                          </td>

                          {/* 2열: 제목 (회색 꺾쇠 + 회색 차수 뱃지 + 제목 + 지시서명) */}
                          <td style={{ paddingLeft: '16px' }}>
                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, width: '100%' }}>
                              <span className="text-secondary flex-shrink-0" style={{ fontSize: '0.75rem', opacity: 0.65 }}>
                                <i className="fas fa-level-up-alt fa-rotate-90" />
                              </span>
                              {Boolean(item.is_final || Number(item.progress_rate) === 100) ? (
                                <span className="badge bg-success text-white border-0 px-1 flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                  최종결과
                                </span>
                              ) : (
                                <span className="badge bg-light text-secondary border px-1 flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                  {item.report_seq || 1}차 결과
                                </span>
                              )}
                              <div
                                className="text-truncate"
                                style={{
                                  minWidth: 0,
                                  flex: '1 1 auto',
                                  fontSize: '0.82rem',
                                  fontWeight: 500,
                                  color: '#475569'
                                }}
                                title={item.report_title}
                              >
                                {item.report_title || `${item.report_seq || 1}차 처리결과`}
                              </div>
                              {parentWO.title && (
                                <span
                                  className="text-truncate flex-shrink-0"
                                  style={{
                                    fontSize: '0.74rem',
                                    maxWidth: '185px',
                                    color: '#2563eb',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                  }}
                                  title={`원문 업무협조 열람: ${parentWO.title}`}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = 'underline';
                                    e.currentTarget.style.color = '#1d4ed8';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = 'none';
                                    e.currentTarget.style.color = '#2563eb';
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openViewModal(item.work_order_id || parentWO.id, false);
                                  }}
                                >
                                  (원문 업무협조: {parentWO.title})
                                </span>
                              )}

                              {/* 비공개 뱃지 */}
                              {(item.is_public === false || item.is_public === 0) && (
                                <span className="badge bg-warning text-dark px-2 py-1" style={{ fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center' }}>
                                  <i className="fas fa-lock me-1" style={{ fontSize: '0.65rem' }} />비공개
                                </span>
                              )}
                            </div>
                          </td>

                          {/* 3열: 지시자 (업무지시 지시자 정보 동일 UI로 표시) */}
                          <td style={{ textAlign: 'center' }}>
                            {renderAuthor(parentWO.author)}
                          </td>

                          {/* 4열: 담당자(보고자) (기존 하위 보고서와 100% 동일한 녹색 아이콘 칩) */}
                          <td style={{ textAlign: 'center' }}>
                            <span className="badge bg-white text-dark border px-2 py-1" style={{ fontSize: '0.78rem' }}>
                              <i className="fas fa-user-edit me-1" style={{ color: '#0d9488' }} />
                              {reporterDisplayText}
                            </span>
                          </td>

                          {/* 5열: D-Day */}
                          <td style={{ textAlign: 'center', fontSize: '0.82rem' }}>
                            {(() => {
                              if (parentWO.deleted_at || parentWO.status === 'deleted') {
                                return <span className="text-secondary fw-bold">삭제</span>;
                              }
                              if (['cancel', 'canceled'].includes(parentWO.status)) {
                                return <span className="text-secondary fw-bold">취소</span>;
                              }
                              if (parentWO.status === 'stop') {
                                return <span className="text-warning fw-bold">중단</span>;
                              }
                              if (['approved', 'completed', 'done'].includes(repStatus)) {
                                return <span className="text-success fw-bold">완료</span>;
                              }
                              const repDDay = getDDay(parentWO.due_date, parentWO);
                              return repDDay ? (
                                <span className={repDDay.className}>{repDDay.text}</span>
                              ) : (
                                <span className="text-muted">-</span>
                              );
                            })()}
                          </td>

                          {/* 6열: 진척률 (승인완료: 선명한 실선 / 미승인: 빗금 스트라이프) */}
                          <td style={{ textAlign: 'center' }}>
                            {(() => {
                              const isRepApproved = ['approved', 'completed', 'done'].includes(repStatus);
                              return (
                                <div className="d-flex align-items-center gap-1 mx-auto" style={{ minWidth: '70px', maxWidth: '85px' }}>
                                  <div className="progress flex-grow-1" style={{ height: '6px', borderRadius: '4px', backgroundColor: '#e2e8f0' }}>
                                    <div
                                      className={`progress-bar ${isRepApproved ? (isComplete ? 'bg-success' : 'bg-primary') : 'bg-primary'}`}
                                      style={{
                                        width: `${repProgressRate}%`,
                                        opacity: isRepApproved ? 1 : 0.35
                                      }}
                                    />
                                  </div>
                                  <span
                                    className="small fw-bold"
                                    style={{
                                      fontSize: '0.7rem',
                                      minWidth: '28px',
                                      color: isRepApproved ? (isComplete ? '#16a34a' : '#2563eb') : '#94a3b8'
                                    }}
                                  >
                                    {repProgressRate}%
                                  </span>
                                </div>
                              );
                            })()}
                          </td>

                          {/* 7열: 상태 (기존 하위 보고서 동일 상태 뱃지 및 팝오버) */}
                          <td style={{ textAlign: 'center' }}>
                            <div
                              style={{ display: 'inline-block', cursor: 'pointer' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
                                const rect = e.currentTarget.getBoundingClientRect();
                                const popoverWidth = 290;
                                const spaceLeft = rect.left;
                                const spaceRight = window.innerWidth - rect.right;
                                const showRight = spaceLeft < popoverWidth && spaceRight >= spaceLeft;

                                setHoveredStatus({
                                  category: '결과',
                                  label: conf.label,
                                  badgeBg: conf.badgeBg,
                                  message: conf.message,
                                  x: showRight ? (rect.right + 8) : (rect.left - 8),
                                  y: rect.top + rect.height / 2,
                                  badgeY: rect.top + rect.height / 2,
                                  showRight
                                });
                              }}
                              onMouseLeave={() => {
                                hoverStatusTimerRef.current = setTimeout(() => {
                                  setHoveredStatus(null);
                                }, 180);
                              }}
                            >
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  fontSize: '0.72rem',
                                  fontWeight: 600,
                                  borderRadius: '12px',
                                  backgroundColor: conf.bg,
                                  color: conf.color,
                                  border: `1px solid ${conf.border}`,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {conf.label}
                              </span>
                            </div>
                          </td>

                          {/* 8열: 액션 (기존 보고서 동일 [열람/검토] 버튼) */}
                          <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            {(() => {
                              const isDraft = repStatus === 'draft' || repStatus === 'temp';
                              const isRejected = repStatus === 'reject' || repStatus === 'rejected';
                              const currentUidVal = String(user?.id || user?.userid || '');
                              const parentAuthorIdVal = String(parentWO.author_id || parentWO.author?.id || parentWO.author?.userid || '');
                              const isParentAuthor = Boolean(currentUidVal && (parentAuthorIdVal === currentUidVal || parentWO.author?.name === user?.name));
                              const isMyRep = String(item.user_id || item.reporter?.id || item.reporter?.userid || '') === currentUidVal;
                              const isReviewPending = (repStatus === 'review' || repStatus === 'submitted' || !repStatus || repStatus === 'pending') && !isDraft;

                              let pulseClass = '';
                              if (isParentAuthor && isReviewPending) {
                                pulseClass = 'wo-pulse-purple';
                              } else if (isMyRep && isRejected) {
                                pulseClass = 'wo-pulse-red';
                              } else if (isMyRep && isDraft) {
                                pulseClass = 'wo-pulse-blue';
                              }

                              return (
                                <button
                                  type="button"
                                  className={`btn ${isDraft ? 'btn-outline-secondary' : (isRejected && isMyRep ? 'btn-outline-danger' : 'btn-outline-primary')} ${pulseClass}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '3px 9px',
                                    fontSize: '0.74rem',
                                    fontWeight: 600,
                                    borderRadius: '4px',
                                    lineHeight: 1.5,
                                    whiteSpace: 'nowrap'
                                  }}
                                  onClick={() => {
                                    setSelectedReport(item);
                                    setReportParentWorkOrderId(item.work_order_id);
                                    setReportDetailOpen(true);
                                  }}
                                >
                                  {isDraft ? '이어작성' : (isParentAuthor && isReviewPending ? '검토' : '열람')}
                                </button>
                              );
                            })()}
                          </td>

                          {/* 9열: 작성일 */}
                          <td style={{ textAlign: 'center', fontSize: '0.8rem', color: '#64748b' }}>
                            {formatCreatedAt(item.submitted_at || item.created_at)}
                          </td>
                        </tr>
                      );
                    }

                    const dday = getDDay(item.due_date, item);
                    const currentUserIdStr = String(user?.id || user?.userid || '');
                    const isAdmin = user?.is_admin === true || user?.is_admin === 1 || user?.role === 'admin';
                    const visibleReports = (item.reports || [])
                      .filter(rep => {
                        const repStatus = rep.approval_status || rep.status;
                        const repUserId = String(rep.user_id || rep.reporter?.id || rep.reporter?.userid || '');
                        if (repStatus === 'draft' || repStatus === 'temp') {
                          const canViewDraft = isAdmin || (currentUserIdStr && repUserId === currentUserIdStr);
                          if (!canViewDraft) return false;
                        }

                        // 비공개 보고서 권한 체크: 작성자 본인, 지시자, 관리자만 열람/노출 가능
                        const isPrivate = rep.is_public === false || rep.is_public === 0;
                        if (isPrivate) {
                          const isAuthor = String(item.author_id) === currentUserIdStr;
                          const isMyRep = repUserId === currentUserIdStr;
                          if (!isAdmin && !isAuthor && !isMyRep) return false;
                        }

                        // ⚡ 조치 필요 필터 활성화 시 불필요한 보고서 숨김 처리
                        if (selectedStatus === 'action_required') {
                          if (tab === 'received') {
                            // 지시받은 업무: 오직 내 보고서 중 보완요청(반려) 또는 임시저장만 표출
                            const isMine = currentUserIdStr && repUserId === currentUserIdStr;
                            const isActionNeeded = ['rejected', 'reject', 'draft', 'temp'].includes(repStatus);
                            return isMine && isActionNeeded;
                          } else if (tab === 'sent') {
                            // 지시한 업무: 지시자가 검토/승인해야 하는 보고서만 표출
                            return ['submitted', 'pending', 'review'].includes(repStatus);
                          }
                        }

                        return true;
                      })
                      .sort((a, b) => {
                        // 1차: 차수(report_seq) 내림차순 (최신 차수 우선, 1차가 맨 아래로)
                        const seqA = Number(a.report_seq || 1);
                        const seqB = Number(b.report_seq || 1);
                        if (seqA !== seqB) {
                          return seqB - seqA;
                        }

                        // 2차: 작성자 성명 오름차순 (가나다순)
                        const nameA = String(a.reporter?.name || item.assignees?.find(x => String(x.user_id) === String(a.user_id))?.user?.name || '').trim();
                        const nameB = String(b.reporter?.name || item.assignees?.find(x => String(x.user_id) === String(b.user_id))?.user?.name || '').trim();
                        return nameA.localeCompare(nameB, 'ko');
                      });
                    const hasReports = visibleReports.length > 0;
                    const isExpanded = Boolean(expandedRows[item.id]);

                    return (
                      <React.Fragment key={item.id}>
                        {/* ── [부모 행 / Parent Row] 업무지시서 ── */}
                        <tr
                          className={hasReports && isExpanded ? 'work-order-parent-expanded' : ''}
                          onClick={() => {
                            const isTemp = item.status === 'temp' || item.status === 'draft' || tab === 'temp';
                            const defaultTab = tab === 'reports' ? 'reports' : null;
                            openViewModal(item.id, isTemp, defaultTab);
                          }}
                          style={{
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            backgroundColor: hasReports && isExpanded ? '#fafafa' : 'transparent'
                          }}
                        >
                          {/* NO */}
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '28px',
                                height: '24px',
                                padding: '0 6px',
                                backgroundColor: '#f1f5f9',
                                borderRadius: '6px',
                                color: '#64748b',
                                fontWeight: 600,
                                fontSize: '0.8rem'
                              }}
                            >
                              {rowNum}
                            </span>
                          </td>

                          {/* 제목 */}
                          <td style={{ paddingLeft: '16px' }}>
                            <div className="d-flex align-items-center gap-2">
                              {/* 보고서가 있을 때만 트리 접기/펼치기 토글 버튼 표시 */}
                              {hasReports && (
                                <button
                                  type="button"
                                  className="p-0 me-1 text-secondary d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{
                                    textDecoration: 'none',
                                    fontSize: '0.72rem',
                                    width: '18px',
                                    height: '18px',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '3px',
                                    backgroundColor: '#fff',
                                    outline: 'none',
                                    boxShadow: 'none',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s ease, border-color 0.15s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                                    e.currentTarget.style.borderColor = '#94a3b8';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.style.borderColor = '#cbd5e1';
                                  }}
                                  onClick={(e) => {
                                    if (e.currentTarget) e.currentTarget.blur();
                                    toggleExpand(item.id, e);
                                  }}
                                  title={isExpanded ? '처리결과 목록 접기' : '처리결과 목록 펼치기'}
                                >
                                  <i className={`fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                                </button>
                              )}

                              {renderPriorityBadge(item.priority)}
                              <div className="text-truncate fw-bold" style={{ minWidth: 0, fontSize: '0.88rem', color: '#0f172a' }} title={item.title}>
                                {item.title}
                              </div>

                              {/* 보고서 카운트 뱃지 (높이 18px, 좌우 패딩 4.5px로 컴팩트 조정) */}
                              {hasReports && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: '#0d9488',
                                    color: '#fff',
                                    fontSize: '0.63rem',
                                    fontWeight: 600,
                                    height: '18px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '0 4.5px',
                                    letterSpacing: '-0.01em',
                                    lineHeight: 1
                                  }}
                                  title={`제출된 처리결과 ${visibleReports.length}건`}
                                >
                                  결과 {visibleReports.length}
                                </span>
                              )}

                              {(() => {
                                const itemFiles = (item.files || []).filter(f => !f.report_id);
                                if (itemFiles.length === 0) return null;
                                return (
                                  <>
                                    <div
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '4px 6px',
                                        borderRadius: '4px',
                                        background: filePopover.id === item.id ? '#dbeafe' : 'transparent',
                                        color: filePopover.id === item.id ? '#2563eb' : '#94a3b8',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        transition: 'all 0.2s ease'
                                      }}
                                      title={`첨부파일 (${itemFiles.length})`}
                                      onMouseOver={(e) => {
                                        if (filePopover.id !== item.id) {
                                          e.currentTarget.style.background = '#eff6ff';
                                          e.currentTarget.style.color = '#2563eb';
                                        }
                                      }}
                                      onMouseOut={(e) => {
                                        if (filePopover.id !== item.id) {
                                          e.currentTarget.style.background = 'transparent';
                                          e.currentTarget.style.color = '#94a3b8';
                                        }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (filePopover.id === item.id) {
                                          setFilePopover({ id: null, x: 0, y: 0, isUp: false });
                                        } else {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          const popoverHeight = Math.min(itemFiles.length * 36, 180) + 110;
                                          const spaceBelow = window.innerHeight - rect.bottom;
                                          const showUp = spaceBelow < popoverHeight && rect.top > popoverHeight;

                                          setFilePopover({
                                            id: item.id,
                                            itemTitle: item.title,
                                            files: itemFiles,
                                            x: rect.left,
                                            isUp: showUp,
                                            top: rect.bottom + 4,
                                            bottom: window.innerHeight - rect.top + 4
                                          });
                                        }
                                      }}
                                    >
                                      <i className="fas fa-paperclip" style={{ fontSize: '0.82rem' }} />
                                    </div>

                                    {/* 팝오버 UI (createPortal 최상위 body 렌더링) */}
                                    {filePopover.id === item.id && createPortal(
                                      <div
                                        style={{
                                          position: 'fixed',
                                          top: filePopover.isUp ? 'auto' : `${filePopover.top}px`,
                                          bottom: filePopover.isUp ? `${filePopover.bottom}px` : 'auto',
                                          left: `${filePopover.x}px`,
                                          background: 'white',
                                          border: '1px solid #cbd5e1',
                                          borderRadius: '6px',
                                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                          zIndex: 9999,
                                          width: '360px',
                                          padding: '10px',
                                          cursor: 'default'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#e2e8f0', margin: '-10px -10px 10px -10px', padding: '8px 12px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', borderBottom: '1px solid #cbd5e1' }}>
                                          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>첨부파일 목록 ({itemFiles.length})</span>
                                          <i className="fas fa-times" style={{ cursor: 'pointer', color: '#64748b', fontSize: '0.9rem' }} onClick={() => setFilePopover({ id: null, x: 0, y: 0, isUp: false })}></i>
                                        </div>
                                        <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                          {itemFiles.map((mf, mfIdx) => {
                                            const fName = mf.file_name || mf.originalName || mf.name || '첨부파일';
                                            const fSize = Number(mf.file_size || mf.size || 0);
                                            return (
                                              <div key={mf.id || mfIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: '4px', fontSize: '0.75rem', color: '#475569', background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                                <a href="#!" onClick={(e) => { e.preventDefault(); handleViewFile(mf); }} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '230px', color: '#1e293b', textDecoration: 'none', display: 'flex', alignItems: 'center' }} title={`${fName} 열기`}>
                                                  <i className="far fa-file-alt" style={{ marginRight: '6px', color: '#64748b' }}></i>
                                                  <span style={{ cursor: 'pointer' }} onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'} onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}>{fName}</span>
                                                </a>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                  <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{formatBytes(fSize)}</span>
                                                  <i className="fas fa-download" style={{ color: '#2563eb', cursor: 'pointer' }} title="다운로드" onClick={() => handleDownloadFile(mf)}></i>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                                          <button
                                            type="button"
                                            onClick={() => handleZipDownloadFiles(itemFiles, item.title)}
                                            style={{
                                              width: '100%', padding: '7px 0', background: '#eff6ff', border: '1px solid #bfdbfe',
                                              borderRadius: '4px', color: '#1d4ed8', fontSize: '0.75rem', fontWeight: 700,
                                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                            }}
                                          >
                                            <i className="fas fa-file-archive" style={{ color: '#ea580c', fontSize: '0.85rem' }}></i>
                                            {itemFiles.length === 1 ? '압축(ZIP) 다운로드' : '전체 압축(ZIP) 다운로드'}
                                          </button>
                                        </div>
                                      </div>,
                                      document.body
                                    )}
                                  </>
                                );
                              })()}
                              {item.comments?.length > 0 && (
                                <span className="text-muted small flex-shrink-0" style={{ fontSize: '0.72rem' }}>
                                  <i className="fas fa-comment me-1" />{item.comments.length}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* 지시자 */}
                          <td style={{ textAlign: 'center' }}>
                            {renderAuthor(item.author)}
                          </td>

                          {/* 담당자 */}
                          <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            {renderAssignees(item.assignees)}
                          </td>

                          {/* D-Day */}
                          <td style={{ textAlign: 'center' }}>
                            {dday ? (
                              <span className={`small ${dday.className}`} style={{ fontSize: '0.78rem' }}>
                                {dday.text}
                              </span>
                            ) : '-'}
                          </td>

                          {/* 진척률 */}
                          <td style={{ textAlign: 'center' }}>
                            {renderProgress(item.assignees, item.status, item.reports)}
                          </td>

                          {/* 상태 */}
                          <td style={{ textAlign: 'center' }}>
                            {renderStatusBadge(item)}
                          </td>

                          {/* 액션 */}
                          <td style={{ textAlign: 'center', cursor: 'default' }} onClick={e => e.stopPropagation()}>
                            {renderActions(item)}
                          </td>

                          {/* 작성일 */}
                          <td style={{ textAlign: 'center', fontSize: '0.82rem', color: '#64748b' }}>
                            {formatCreatedAt(item.created_at)}
                          </td>
                        </tr>

                        {/* ── [자식 행 / Child Rows] 결과보고서 (응답문서) 계층 렌더링 ── */}
                        {hasReports && isExpanded && visibleReports.map((rep, rIdx) => {
                          const isPrivate = rep.is_public === false || rep.is_public === 0;
                          const isLastChild = rIdx === visibleReports.length - 1;

                          const matchedAssignee = item.assignees?.find(a => String(a.user_id) === String(rep.user_id || rep.reporter?.id || rep.reporter?.userid));
                          const repPos = rep.reporter?.org?.position || rep.reporter?.position || matchedAssignee?.user?.org?.position || matchedAssignee?.user?.position || '';
                          const repName = rep.reporter?.name || matchedAssignee?.user?.name || '담당자';
                          const reporterDisplayText = `${repName}${repPos ? ' ' + repPos : ''}`;

                          const repProgressRate = rep.progress_rate !== undefined && rep.progress_rate !== null
                            ? Number(rep.progress_rate)
                            : (matchedAssignee?.progress_rate !== undefined && matchedAssignee?.progress_rate !== null ? Number(matchedAssignee.progress_rate) : 100);
                          const isComplete = repProgressRate === 100;

                          return (
                            <tr
                              key={`rep-${rep.id}`}
                              onClick={() => {
                                setSelectedReport(rep);
                                setReportParentWorkOrderId(item.id);
                                setReportDetailOpen(true);
                              }}
                              style={{
                                backgroundColor: '#f8fafc',
                                cursor: 'pointer',
                                borderBottom: isLastChild ? '1px solid #e2e8f0' : '1px dashed #e2e8f0',
                                transition: 'background-color 0.15s ease'
                              }}
                              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                            >
                              {/* NO 컬럼 (A안: 앞쪽 화살표 제거하여 빈 칸 유지) */}
                              <td style={{ textAlign: 'center' }}></td>

                              {/* 제목 컬럼 (A안: 텍스트 └ 대신 꺾인 화살표 아이콘으로 교체, 1차 보고 뱃지 앞 문서 아이콘 제거) */}
                              <td style={{ paddingLeft: '36px' }}>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="text-secondary" style={{ fontSize: '0.75rem', opacity: 0.65 }}>
                                    <i className="fas fa-level-up-alt fa-rotate-90" />
                                  </span>
                                  {Boolean(rep.is_final || Number(rep.progress_rate) === 100) ? (
                                    <span className="badge bg-success text-white border-0 px-1" style={{ fontSize: '0.7rem' }}>
                                      최종결과
                                    </span>
                                  ) : (
                                    <span className="badge bg-light text-secondary border px-1" style={{ fontSize: '0.7rem' }}>
                                      {rep.report_seq || (rIdx + 1)}차 결과
                                    </span>
                                  )}
                                  <div
                                    className="text-truncate"
                                    style={{
                                      minWidth: 0,
                                      fontSize: '0.82rem',
                                      fontWeight: 500,
                                      color: '#475569'
                                    }}
                                    title={rep.report_title}
                                  >
                                    {rep.report_title || `${rep.report_seq || (rIdx + 1)}차 처리결과`}
                                  </div>

                                  {/* 비공개 뱃지 */}
                                  {isPrivate && (
                                    <span className="badge bg-warning text-dark px-2 py-1" style={{ fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center' }}>
                                      <i className="fas fa-lock me-1" style={{ fontSize: '0.65rem' }} />비공개
                                    </span>
                                  )}

                                  {/* 첨부 산출물 파일 팝오버 */}
                                  {rep.files && rep.files.length > 0 && (() => {
                                    const repFiles = rep.files;
                                    const popoverKey = `rep_${rep.id || rIdx}`;
                                    const repTitle = rep.report_title || `${rep.report_seq || (rIdx + 1)}차 처리결과`;
                                    return (
                                      <>
                                        <div
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '3px 5px',
                                            borderRadius: '4px',
                                            background: filePopover.id === popoverKey ? '#dbeafe' : 'transparent',
                                            color: filePopover.id === popoverKey ? '#2563eb' : '#94a3b8',
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            transition: 'all 0.2s ease'
                                          }}
                                          title={`첨부파일 (${repFiles.length})`}
                                          onMouseOver={(e) => {
                                            if (filePopover.id !== popoverKey) {
                                              e.currentTarget.style.background = '#eff6ff';
                                              e.currentTarget.style.color = '#2563eb';
                                            }
                                          }}
                                          onMouseOut={(e) => {
                                            if (filePopover.id !== popoverKey) {
                                              e.currentTarget.style.background = 'transparent';
                                              e.currentTarget.style.color = '#94a3b8';
                                            }
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (filePopover.id === popoverKey) {
                                              setFilePopover({ id: null, x: 0, y: 0, isUp: false });
                                            } else {
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              const popoverHeight = Math.min(repFiles.length * 36, 180) + 110;
                                              const spaceBelow = window.innerHeight - rect.bottom;
                                              const showUp = spaceBelow < popoverHeight && rect.top > popoverHeight;

                                              setFilePopover({
                                                id: popoverKey,
                                                itemTitle: repTitle,
                                                files: repFiles,
                                                x: rect.left,
                                                isUp: showUp,
                                                top: rect.bottom + 4,
                                                bottom: window.innerHeight - rect.top + 4
                                              });
                                            }
                                          }}
                                        >
                                          <i className="fas fa-paperclip" style={{ fontSize: '0.78rem' }} />
                                        </div>

                                        {filePopover.id === popoverKey && createPortal(
                                          <div
                                            style={{
                                              position: 'fixed',
                                              top: filePopover.isUp ? 'auto' : `${filePopover.top}px`,
                                              bottom: filePopover.isUp ? `${filePopover.bottom}px` : 'auto',
                                              left: `${filePopover.x}px`,
                                              background: 'white',
                                              border: '1px solid #cbd5e1',
                                              borderRadius: '6px',
                                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                              zIndex: 9999,
                                              width: '360px',
                                              padding: '10px',
                                              cursor: 'default'
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#e2e8f0', margin: '-10px -10px 10px -10px', padding: '8px 12px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', borderBottom: '1px solid #cbd5e1' }}>
                                              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>첨부파일 목록 ({repFiles.length})</span>
                                              <i className="fas fa-times" style={{ cursor: 'pointer', color: '#64748b', fontSize: '0.9rem' }} onClick={() => setFilePopover({ id: null, x: 0, y: 0, isUp: false })}></i>
                                            </div>
                                            <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                              {repFiles.map((mf, mfIdx) => {
                                                const fName = mf.file_name || mf.originalName || mf.name || '첨부파일';
                                                const fSize = Number(mf.file_size || mf.size || 0);
                                                return (
                                                  <div key={mf.id || mfIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: '4px', fontSize: '0.75rem', color: '#475569', background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                                    <a href="#!" onClick={(e) => { e.preventDefault(); handleViewFile(mf); }} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '230px', color: '#1e293b', textDecoration: 'none', display: 'flex', alignItems: 'center' }} title={`${fName} 열기`}>
                                                      <i className="far fa-file-alt" style={{ marginRight: '6px', color: '#64748b' }}></i>
                                                      <span style={{ cursor: 'pointer' }} onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'} onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}>{fName}</span>
                                                    </a>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                      <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{formatBytes(fSize)}</span>
                                                      <i className="fas fa-download" style={{ color: '#2563eb', cursor: 'pointer' }} title="다운로드" onClick={() => handleDownloadFile(mf)}></i>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                                              <button
                                                type="button"
                                                onClick={() => handleZipDownloadFiles(repFiles, repTitle)}
                                                style={{
                                                  width: '100%', padding: '7px 0', background: '#eff6ff', border: '1px solid #bfdbfe',
                                                  borderRadius: '4px', color: '#1d4ed8', fontSize: '0.75rem', fontWeight: 700,
                                                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                                }}
                                              >
                                                <i className="fas fa-file-archive" style={{ color: '#ea580c', fontSize: '0.85rem' }}></i>
                                                {repFiles.length === 1 ? '압축(ZIP) 다운로드' : '전체 압축(ZIP) 다운로드'}
                                              </button>
                                            </div>
                                          </div>,
                                          document.body
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </td>

                              {/* 지시자 컬럼 (보고서 행은 공백 유지) */}
                              <td></td>

                              {/* 담당자(보고 작성자) 컬럼 (성명 + 직위 표기) */}
                              <td style={{ textAlign: 'center' }}>
                                <span className="badge bg-white text-dark border px-2 py-1" style={{ fontSize: '0.78rem' }}>
                                  <i className="fas fa-user-edit me-1" style={{ color: '#0d9488' }} />
                                  {reporterDisplayText}
                                </span>
                              </td>

                              {/* D-Day 컬럼 */}
                              <td style={{ textAlign: 'center', fontSize: '0.82rem' }}>
                                {(() => {
                                  if (item.deleted_at || item.status === 'deleted') {
                                    return <span className="text-secondary fw-bold">삭제</span>;
                                  }
                                  if (['cancel', 'canceled'].includes(item.status)) {
                                    return <span className="text-secondary fw-bold">취소</span>;
                                  }
                                  if (item.status === 'stop') {
                                    return <span className="text-warning fw-bold">중단</span>;
                                  }
                                  const repStatus = rep.approval_status || rep.status;
                                  if (['approved', 'completed', 'done'].includes(repStatus)) {
                                    return <span className="text-success fw-bold">완료</span>;
                                  }
                                  const repDueDate = matchedAssignee?.end_date || item.due_date;
                                  const repDDay = getDDay(repDueDate);
                                  return repDDay ? (
                                    <span className={repDDay.className}>{repDDay.text}</span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  );
                                })()}
                              </td>

                              {/* 진척률 컬럼 (승인완료: 선명한 실선 / 미승인: 빗금 스트라이프) */}
                              <td style={{ textAlign: 'center' }}>
                                {(() => {
                                  const repApprovalStatus = rep.approval_status || rep.status;
                                  const isRepApproved = ['approved', 'completed', 'done'].includes(repApprovalStatus);
                                  return (
                                    <div className="d-flex align-items-center gap-1 mx-auto" style={{ minWidth: '70px', maxWidth: '85px' }}>
                                      <div className="progress flex-grow-1" style={{ height: '6px', borderRadius: '4px', backgroundColor: '#e2e8f0' }}>
                                        <div
                                          className={`progress-bar ${isRepApproved ? (isComplete ? 'bg-success' : 'bg-primary') : 'bg-primary'}`}
                                          style={{
                                            width: `${repProgressRate}%`,
                                            opacity: isRepApproved ? 1 : 0.35
                                          }}
                                        />
                                      </div>
                                      <span
                                        className="small fw-bold"
                                        style={{
                                          fontSize: '0.7rem',
                                          minWidth: '28px',
                                          color: isRepApproved ? (isComplete ? '#16a34a' : '#2563eb') : '#94a3b8'
                                        }}
                                      >
                                        {repProgressRate}%
                                      </span>
                                    </div>
                                  );
                                })()}
                              </td>

                              {/* 상태 컬럼 */}
                              <td style={{ textAlign: 'center' }}>
                                {(() => {
                                  const status = rep.approval_status || rep.status;
                                  let conf = {
                                    label: '검토대기',
                                    bg: '#f5f3ff',
                                    color: '#6d28d9',
                                    border: '#ddd6fe',
                                    badgeBg: '#6d28d9',
                                    message: '처리결과가 제출되어 요청자의 검토 및 확인을 기다리는 상태입니다.'
                                  };
                                  if (status === 'approved' || status === 'completed' || status === 'done') {
                                    conf = {
                                      label: '확인완료',
                                      bg: '#f0fdf4',
                                      color: '#15803d',
                                      border: '#bbf7d0',
                                      badgeBg: '#15803d',
                                      message: '요청자가 처리결과를 최종 확인완료한 상태입니다.'
                                    };
                                  } else if (status === 'rejected' || status === 'reject') {
                                    conf = {
                                      label: '보완요청',
                                      bg: '#fef2f2',
                                      color: '#b91c1c',
                                      border: '#fecaca',
                                      badgeBg: '#b91c1c',
                                      message: '요청자가 처리결과 검토 후 보완(수정)을 요청한 상태입니다.'
                                    };
                                  } else if (status === 'draft' || status === 'temp') {
                                    conf = {
                                      label: '임시저장',
                                      bg: '#f1f5f9',
                                      color: '#64748b',
                                      border: '#e2e8f0',
                                      badgeBg: '#64748b',
                                      message: '작성 중 임시저장된 초안으로, 본인만 열람 및 이어작성할 수 있습니다.'
                                    };
                                  }

                                  return (
                                    <div
                                      style={{ display: 'inline-block', cursor: 'pointer' }}
                                      onMouseEnter={(e) => {
                                        e.stopPropagation();
                                        if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const popoverWidth = 290;
                                        const spaceLeft = rect.left;
                                        const spaceRight = window.innerWidth - rect.right;
                                        const showRight = spaceLeft < popoverWidth && spaceRight >= spaceLeft;

                                        setHoveredStatus({
                                          category: '결과',
                                          label: conf.label,
                                          badgeBg: conf.badgeBg,
                                          message: conf.message,
                                          x: showRight ? (rect.right + 8) : (rect.left - 8),
                                          y: rect.top + rect.height / 2,
                                          badgeY: rect.top + rect.height / 2,
                                          showRight
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        hoverStatusTimerRef.current = setTimeout(() => {
                                          setHoveredStatus(null);
                                        }, 180);
                                      }}
                                    >
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          padding: '2px 8px',
                                          fontSize: '0.72rem',
                                          fontWeight: 600,
                                          borderRadius: '12px',
                                          backgroundColor: conf.bg,
                                          color: conf.color,
                                          border: `1px solid ${conf.border}`,
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {conf.label}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </td>

                              {/* 액션 컬럼 (당사자 결재 대기 시 wo-pulse-purple 깜빡임) */}
                              <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                {(() => {
                                  const status = rep.approval_status || rep.status;
                                  const isDraft = status === 'draft' || status === 'temp';
                                  const isRejected = status === 'reject' || status === 'rejected';
                                  const currentUidVal = String(user?.id || user?.userid || '');
                                  const isParentAuthor = Boolean(currentUidVal && (String(item.author_id) === currentUidVal || String(item.author?.userid) === currentUidVal || item.author?.name === user?.name));
                                  const isMyRep = String(rep.user_id || rep.reporter?.id || rep.reporter?.userid || '') === currentUidVal;
                                  const isReviewPending = (status === 'review' || status === 'submitted' || !status || status === 'pending') && !isDraft;

                                  let pulseClass = '';
                                  if (isParentAuthor && isReviewPending) {
                                    pulseClass = 'wo-pulse-purple';
                                  } else if (isMyRep && isRejected) {
                                    pulseClass = 'wo-pulse-red';
                                  } else if (isMyRep && isDraft) {
                                    pulseClass = 'wo-pulse-blue';
                                  }

                                  return (
                                    <button
                                      type="button"
                                      className={`btn ${isDraft ? 'btn-outline-secondary' : (isRejected && isMyRep ? 'btn-outline-danger' : 'btn-outline-primary')} ${pulseClass}`}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '3px 9px',
                                        fontSize: '0.74rem',
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                        lineHeight: 1.5,
                                        whiteSpace: 'nowrap'
                                      }}
                                      onClick={() => {
                                        setSelectedReport(rep);
                                        setReportParentWorkOrderId(item.id);
                                        setReportDetailOpen(true);
                                      }}
                                    >
                                      {isDraft ? '이어작성' : (isParentAuthor && isReviewPending ? '검토' : '열람')}
                                    </button>
                                  );
                                })()}
                              </td>

                              {/* 작성일 컬럼 */}
                              <td style={{ textAlign: 'center', fontSize: '0.8rem', color: '#64748b' }}>
                                {formatCreatedAt(rep.submitted_at || rep.created_at)}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── 하단 페이징 영역 (두 번째 표준 이미지 완벽 일치) ── */}
          <div
            className="p-3 border-top d-flex justify-content-between align-items-center position-relative"
            style={{ minHeight: '60px', padding: '14px 22px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}
          >
            {/* 좌측 타원형 뱃지 */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '0.8rem',
                color: '#1e3a8a',
                fontWeight: 600,
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
              }}
            >
              <span style={{ color: total === 0 ? '#94a3b8' : '#2563eb' }}>{total === 0 ? 0 : page}</span>
              <span style={{ color: '#93c5fd' }}>/</span>
              <span>{total === 0 ? 0 : totalPages}</span>
              <span style={{ color: '#bfdbfe' }}>|</span>
              <span>총 {total}건</span>
            </div>

            {/* 중앙 페이지네이션 네비게이션 */}
            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px', alignItems: 'center' }}>
              {/* 처음으로 */}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage(1)}
                disabled={page <= 1 || total === 0}
                style={{
                  minWidth: '34px',
                  height: '34px',
                  borderRadius: '4px',
                  padding: 0,
                  opacity: (page <= 1 || total === 0) ? 0.4 : 1,
                  cursor: (page <= 1 || total === 0) ? 'default' : 'pointer'
                }}
                title="처음으로"
              >
                <i className="fas fa-angle-double-left" />
              </button>
              {/* 이전 (이전 묶음의 마지막 페이지) */}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage(blockStart - 1)}
                disabled={blockStart <= 1 || total === 0}
                style={{
                  minWidth: '34px',
                  height: '34px',
                  borderRadius: '4px',
                  padding: 0,
                  opacity: (blockStart <= 1 || total === 0) ? 0.4 : 1,
                  cursor: (blockStart <= 1 || total === 0) ? 'default' : 'pointer'
                }}
                title="이전"
              >
                <i className="fas fa-chevron-left" />
              </button>

              {/* 페이지 번호 목록 */}
              {(() => {
                const pages = [];
                for (let i = blockStart; i <= blockEnd; i++) pages.push(i);

                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    disabled={total === 0}
                    style={{
                      minWidth: '34px',
                      height: '34px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: page === p && total > 0 ? '#4f46e5' : '#f1f5f9',
                      color: page === p && total > 0 ? '#ffffff' : '#475569',
                      fontWeight: page === p && total > 0 ? 700 : 500,
                      fontSize: '0.85rem',
                      boxShadow: page === p && total > 0 ? '0 2px 4px rgba(79, 70, 229, 0.3)' : 'none',
                      cursor: total === 0 ? 'default' : 'pointer'
                    }}
                  >
                    {p}
                  </button>
                ));
              })()}

              {/* 다음 (다음 묶음의 첫 페이지) */}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage(blockEnd + 1)}
                disabled={blockEnd >= totalPages || total === 0}
                style={{
                  minWidth: '34px',
                  height: '34px',
                  borderRadius: '4px',
                  padding: 0,
                  opacity: (blockEnd >= totalPages || total === 0) ? 0.4 : 1,
                  cursor: (blockEnd >= totalPages || total === 0) ? 'default' : 'pointer'
                }}
                title="다음"
              >
                <i className="fas fa-chevron-right" />
              </button>
              {/* 마지막으로 */}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || total === 0}
                style={{
                  minWidth: '34px',
                  height: '34px',
                  borderRadius: '4px',
                  padding: 0,
                  opacity: (page >= totalPages || total === 0) ? 0.4 : 1,
                  cursor: (page >= totalPages || total === 0) ? 'default' : 'pointer'
                }}
                title="마지막으로"
              >
                <i className="fas fa-angle-double-right" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── 모달 렌더 ── */}
      {modalOpen && modalType === 'detail' && (
        <WorkOrderDetailModal
          open={modalOpen}
          workOrderId={modalWorkOrderId}
          onClose={closeModal}
          onDeleted={handleDeleted}
          onRefreshList={() => {
            fetchList();
            if (tab === 'completed') fetchCompletedCounts();
            else fetchTabCounts();
          }}
          initialTab={modalInitialTab}
          focusId={modalFocusId}
        />
      )}

      {modalOpen && modalType === 'form' && (
        <WorkOrderFormModal
          open={modalOpen}
          mode={modalMode}
          workOrderId={modalWorkOrderId}
          onClose={closeModal}
          onSaved={handleSaved}
          onDelete={handleDeleted}
          onDeleted={handleDeleted}
          onStartEdit={switchToEdit}
        />
      )}

      {/* ── 보고서 상세 조회 및 승인/반려 모달 (목록 자식 행 클릭 시) ── */}
      {reportDetailOpen && selectedReport && (() => {
        const resolvedParentWO = selectedReport.workOrder || list.find(wo => wo.id === reportParentWorkOrderId);
        const resolvedAuthorId = resolvedParentWO?.author_id || resolvedParentWO?.author?.id || resolvedParentWO?.author?.userid;
        const currentUserIdVal = String(user?.id || user?.userid || '');
        const isAuthorVal = Boolean(resolvedAuthorId && String(resolvedAuthorId) === currentUserIdVal);

        return (
          <WorkOrderReportDetailModal
            open={reportDetailOpen}
            workOrderId={reportParentWorkOrderId || selectedReport.work_order_id}
            workOrder={resolvedParentWO}
            report={selectedReport}
            isAuthor={isAuthorVal}
            currentUserId={currentUserIdVal}
            onClose={() => {
              setReportDetailOpen(false);
              setSelectedReport(null);
              setReportParentWorkOrderId(null);
              const urlParams = new URLSearchParams(location.search);
              if (urlParams.has('reportId') || urlParams.has('workOrderId') || urlParams.has('detailId') || urlParams.has('focusId')) {
                urlParams.delete('reportId');
                urlParams.delete('workOrderId');
                urlParams.delete('detailId');
                urlParams.delete('tab');
                urlParams.delete('focusId');
                const newSearch = urlParams.toString();
                navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
              }
            }}
            onReviewed={() => {
              fetchList();
              if (tab === 'completed') fetchCompletedCounts();
              else fetchTabCounts();
              const urlParams = new URLSearchParams(location.search);
              if (urlParams.has('reportId') || urlParams.has('workOrderId') || urlParams.has('detailId') || urlParams.has('focusId')) {
                urlParams.delete('reportId');
                urlParams.delete('workOrderId');
                urlParams.delete('detailId');
                urlParams.delete('tab');
                urlParams.delete('focusId');
                const newSearch = urlParams.toString();
                navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
              }
            }}
          />
        );
      })()}

      {/* ── 독립 결과보고서 작성 모달 (액션 [보고서 작성] 클릭 시 바로 팝업) ── */}
      {reportFormModalState.open && (
        <WorkOrderReportFormModal
          open={reportFormModalState.open}
          workOrderId={reportFormModalState.workOrderId}
          workOrder={reportFormModalState.workOrder}
          workOrderTitle={reportFormModalState.workOrder?.title || ''}
          currentProgress={
            reportFormModalState.workOrder?.assignees?.find(
              a => String(a.user_id || a.M_Code) === String(user?.id || user?.userid)
            )?.progress_rate ?? 0
          }
          reportSeq={
            (() => {
              const myId = String(user?.id || user?.userid || '');
              const mySubmittedCount = (reportFormModalState.workOrder?.reports || []).filter(r => {
                const rId = String(r.user_id || r.reporter?.id || r.reporter?.userid || '');
                return rId === myId && r.approval_status !== 'draft' && r.approval_status !== 'temp';
              }).length;
              return mySubmittedCount + 1;
            })()
          }
          onClose={() => setReportFormModalState({ open: false, workOrderId: null, workOrder: null })}
          onSaved={() => {
            setReportFormModalState({ open: false, workOrderId: null, workOrder: null });
            fetchList();
            if (tab === 'completed') fetchCompletedCounts();
            else fetchTabCounts();
          }}
        />
      )}

      {/* ── 담당자 호버 팝오버 툴팁 (회의록 표준 100% 동일) ── */}
      {hoveredAttendees && createPortal(
        <div
          style={{
            position: 'fixed',
            top: hoveredAttendees.y - 8,
            left: hoveredAttendees.x,
            transform: 'translate(-50%, -100%)',
            backgroundColor: '#2d3748',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
            zIndex: 999999,
            width: 'max-content',
            maxWidth: '320px',
            pointerEvents: 'none'
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            <span className="badge" style={{ backgroundColor: '#2563eb', color: '#fff', fontSize: '0.75rem', padding: '4px 8px' }}>담당자 목록</span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {hoveredAttendees.list.map((name, i) => (
              <span key={i} className="badge" style={{ backgroundColor: '#4a5568', color: '#fff', fontSize: '0.8rem', padding: '6px 10px', fontWeight: 'normal' }}>
                {name}
              </span>
            ))}
          </div>
          <div style={{
            position: 'absolute', top: '100%', left: '50%', marginLeft: '-6px',
            borderWidth: '6px', borderStyle: 'solid', borderColor: '#2d3748 transparent transparent transparent'
          }} />
        </div>,
        document.body
      )}

      {/* ── 상태(접수지연/진행지연) 호버 팝오버 툴팁 (대시보드 표준 100% 동일) ── */}
      {hoveredStatus && (() => {
        const rawY = hoveredStatus.badgeY ?? hoveredStatus.y ?? 0;
        const spaceBottom = window.innerHeight - rawY;
        const spaceTop = rawY;
        const isNearBottom = spaceBottom < 190;
        const isNearTop = !isNearBottom && spaceTop < 120;

        let containerPosStyle = {};
        if (isNearBottom) {
          containerPosStyle = {
            bottom: Math.max(10, window.innerHeight - rawY - 14),
            transform: hoveredStatus.showRight ? 'translate(0, 0)' : 'translate(-100%, 0)'
          };
        } else if (isNearTop) {
          containerPosStyle = {
            top: Math.max(10, rawY - 14),
            transform: hoveredStatus.showRight ? 'translate(0, 0)' : 'translate(-100%, 0)'
          };
        } else {
          containerPosStyle = {
            top: rawY,
            transform: hoveredStatus.showRight ? 'translate(0, -50%)' : 'translate(-100%, -50%)'
          };
        }

        return createPortal(
          <div
            onMouseEnter={() => {
              if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
            }}
            onMouseLeave={() => {
              hoverStatusTimerRef.current = setTimeout(() => {
                setHoveredStatus(null);
              }, 180);
            }}
            style={{
              position: 'fixed',
              left: hoveredStatus.x,
              ...containerPosStyle,
              backgroundColor: '#2d3748',
              borderRadius: '8px',
              padding: '10px 12px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
              zIndex: 999999,
              width: 'max-content',
              maxWidth: '280px',
              pointerEvents: 'auto',
              overscrollBehavior: 'contain',
              color: '#fff',
              fontSize: '0.75rem',
              lineHeight: '1.4'
            }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: hoveredStatus.delayInfo?.type === 'delay' ? '8px' : '0'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              {hoveredStatus.category && (
                <span
                  style={{
                    height: '20px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: hoveredStatus.category === '협조' ? '#2563eb' : '#0d9488',
                    color: '#fff',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    padding: '0 5px',
                    borderRadius: '4px',
                    letterSpacing: '-0.02em',
                    lineHeight: 1
                  }}
                >
                  {hoveredStatus.category}
                </span>
              )}
              <span
                style={{
                  height: '20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: hoveredStatus.badgeBg || (hoveredStatus.label?.includes('접수') ? '#ef4444' : '#f97316'),
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '0 7px',
                  borderRadius: '4px',
                  lineHeight: 1
                }}
              >
                {hoveredStatus.delayInfo?.title || hoveredStatus.label}
              </span>
            </div>
            <i
              className="fas fa-times"
              title="닫기"
              onClick={(e) => {
                e.stopPropagation();
                if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
                setHoveredStatus(null);
              }}
              style={{
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: '0.8rem',
                padding: '2px 4px',
                borderRadius: '4px',
                transition: 'color 0.15s ease, background-color 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#94a3b8';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            />
          </div>

          {(hoveredStatus.delayInfo?.type === 'delay' || hoveredStatus.memberInfo) ? (
            <div className="d-flex flex-column gap-2">
              {(hoveredStatus.delayInfo?.message || hoveredStatus.memberInfo?.message) && (
                <div style={{ color: '#e2e8f0', wordBreak: 'keep-all', fontSize: '0.74rem', lineHeight: '1.45' }}>
                  {hoveredStatus.delayInfo?.message || hoveredStatus.memberInfo?.message}
                </div>
              )}
              {(() => {
                const pendings = hoveredStatus.delayInfo?.pendings || hoveredStatus.memberInfo?.pendings || [];
                const progresses = hoveredStatus.delayInfo?.progresses || hoveredStatus.memberInfo?.progresses || [];
                const dones = hoveredStatus.delayInfo?.dones || hoveredStatus.memberInfo?.dones || [];
                const hasAny = pendings.length > 0 || progresses.length > 0 || dones.length > 0;
                if (!hasAny) return null;

                const hasMsg = Boolean(hoveredStatus.delayInfo?.message || hoveredStatus.memberInfo?.message);

                return (
                  <div
                    style={{
                      maxHeight: '135px',
                      overflowY: 'auto',
                      overscrollBehavior: 'contain',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      paddingRight: '4px',
                      borderTop: hasMsg ? '1px dashed #4a5568' : 'none',
                      paddingTop: hasMsg ? '6px' : '0'
                    }}
                  >
                    {dones.length > 0 && (
                      <div>
                        <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem', color: '#86efac' }}>
                          완료 ({dones.length}명)
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {dones.map((name, i) => (
                            <span
                              key={i}
                              style={{
                                backgroundColor: '#14532d',
                                color: '#bbf7d0',
                                border: '1px solid #22c55e',
                                borderRadius: '3px',
                                padding: '2px 7px',
                                fontSize: '0.73rem',
                                fontWeight: 'normal'
                              }}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {progresses.length > 0 && (
                      <div>
                        <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem', color: '#93c5fd' }}>
                          진행중 ({progresses.length}명)
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {progresses.map((name, i) => (
                            <span
                              key={i}
                              style={{
                                backgroundColor: '#1e3a8a',
                                color: '#bfdbfe',
                                border: '1px solid #3b82f6',
                                borderRadius: '3px',
                                padding: '2px 7px',
                                fontSize: '0.73rem',
                                fontWeight: 'normal'
                              }}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {pendings.length > 0 && (
                      <div>
                        <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem', color: '#fca5a5' }}>
                          미접수 ({pendings.length}명)
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {pendings.map((name, i) => (
                            <span
                              key={i}
                              style={{
                                backgroundColor: '#4a5568',
                                color: '#fff',
                                borderRadius: '3px',
                                padding: '2px 7px',
                                fontSize: '0.73rem',
                                fontWeight: 'normal'
                              }}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{ color: '#e2e8f0', wordBreak: 'keep-all', marginTop: '4px' }}>
              {hoveredStatus.delayInfo?.message || hoveredStatus.message}
            </div>
          )}

          <div style={{
            position: 'absolute',
            ...(isNearBottom ? { bottom: '10px' } : isNearTop ? { top: '10px' } : { top: '50%', marginTop: '-6px' }),
            left: hoveredStatus.showRight ? '-6px' : 'auto',
            right: hoveredStatus.showRight ? 'auto' : '-6px',
            borderWidth: hoveredStatus.showRight ? '6px 6px 6px 0' : '6px 0 6px 6px',
            borderStyle: 'solid',
            borderColor: hoveredStatus.showRight
              ? 'transparent #2d3748 transparent transparent'
              : 'transparent transparent transparent #2d3748'
          }} />
        </div>,
        document.body
      );
    })()}
    </div>
  );
}

export default WorkOrderListPage;
